import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import Select from 'react-select';
import Axios from 'axios';
import { Link, useHistory } from 'react-router-dom';
import url from '../constants/urlsPaths';
import frontendLogger from '../utils/frontendLogger';
import AdminNotifications from './AdminNotifications';

import './Admin.css';

import enFlag from '../ressources/en.webp'; 
import frFlag from '../ressources/fr.webp';
import deFlag from '../ressources/de.webp';
import esFlag from '../ressources/es.webp';
import itFlag from '../ressources/it.webp';
import jaFlag from '../ressources/ja.svg';
import zhFlag from '../ressources/zh.svg';
import zhTWFlag from '../ressources/zh-tw.svg';

const flagMap = {
  en: enFlag,
  fr: frFlag,
  de: deFlag,
  es: esFlag,
  it: itFlag,
    ja: jaFlag,
    zh: zhFlag,
    'zh-TW': zhTWFlag,
};

const CAN_ADD_USERS = [1000];

const languages = [
  { _id: 'fr', name: 'French (fr)' },
  { _id: 'en', name: 'English (en)' },
  { _id: 'es', name: 'Spanish (es)' },
  { _id: 'de', name: 'German (de)' },
        { _id: 'it', name: 'Italian (it)' },
        { _id: 'ja', name: 'Japanese (ja)' },
        { _id: 'zh', name: 'Simplified Chinese (zh)' },
        { _id: 'zh-TW', name: 'Traditional Chinese (zh-TW)' }
];

const roleLabels = {
  en: [
    { value: 'companyContact', label: 'Company Contact' },
    { value: 'coach', label: 'Coach' },
    { value: 'hr_manager', label: 'HR Manager' }
  ],
  fr: [
    { value: 'companyContact', label: 'Contact en entreprise' },
    { value: 'coach', label: 'Coach' },
    { value: 'hr_manager', label: 'Responsable RH' }
  ],
  es: [
    { value: 'companyContact', label: 'Contacto en empresa' },
    { value: 'coach', label: 'Coach' },
    { value: 'hr_manager', label: 'Gerente de RH' }
  ],
  de: [
    { value: 'companyContact', label: 'Kontakt im Unternehmen' },
    { value: 'coach', label: 'Coach' },
    { value: 'hr_manager', label: 'HR-Manager' }
  ],
  it: [
    { value: 'companyContact', label: 'Contatto presso l\'azienda' },
    { value: 'coach', label: 'Coach' },
    { value: 'hr_manager', label: 'Responsabile HR' }
    ],
    zh: [
        { value: 'companyContact', label: '企业联系人' },
        { value: 'coach', label: '教练' },
        { value: 'hr_manager', label: '人力资源经理' }
    ],
    'zh-TW': [
        { value: 'companyContact', label: '企業聯絡人' },
        { value: 'coach', label: '教練' },
        { value: 'hr_manager', label: '人力資源經理' }
  ]
};

export default function Admin() {
    const history = useHistory();
    const modalRef = useRef(null);
    const editModalRef = useRef(null);
    const [openAdminPermissions, setOpenAdminPermissions] = useState(false);
    const [users, setUsers] = useState([{}]);
    const [user, setUser] = useState({});
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [selectedModal, setSelectedModal] = useState(false);
    const [logout, setLogout] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [showApiKeysModal, setShowApiKeysModal] = useState(false);
    const [code, setCode] = useState(undefined);
    const [canAddTests, setCanAddTests] = useState(false);
    const [isTranslator, setIsTranslator] = useState(false);
    const [canReviewQuestionnaire, setCanReviewQuestionnaire] = useState(false);
    const [showTranslationChanges, setShowTranslationChanges] = useState(false);  
    const [translationChanges, setTranslationChanges] = useState(null);
    const [translationLoading, setTranslationLoading] = useState(false);
    const [showUserSelection, setShowUserSelection] = useState(false);
    const [translatorUsers, setTranslatorUsers] = useState([]);
    // API keys UI
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyOwner, setNewKeyOwner] = useState('');
    const [newKeyDesc, setNewKeyDesc] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedLang, setSelectedLang] = useState('fr');
    const [open, setOpen] = useState(false);
    const [licence, setLicence] = useState(5);
    const [selectedUsers, setSelectedUsers] = useState([]);
    // allow multiple selections; empty array = no filter (all)
    const [companyFilter, setCompanyFilter] = useState([]);
    const [adminFilter, setAdminFilter] = useState([]);
    // role filter: 'all' | 'only-admins' | 'only-users' | 'only-promo'
    const [roleFilter, setRoleFilter] = useState('all');

    // pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        try {
            const stored = sessionStorage.getItem('admin_rowsPerPage');
            return stored ? Number(stored) : 50;
        } catch (e) {
            return 50;
        }
    });
    // search term (controlled) to replace DOM search
    const [searchTerm, setSearchTerm] = useState('');
    // show/hide Created column (persisted)
    const [showCreatedColumn, setShowCreatedColumn] = useState(() => {
        try {
            const stored = sessionStorage.getItem('admin_showCreatedColumn');
            return stored === 'true';
        } catch (e) {
            return false;
        }
    });

    // Load persisted filters from sessionStorage on mount
    useEffect(() => {
        try {
            const storedCompany = sessionStorage.getItem('admin_companyFilter');
            const storedAdmin = sessionStorage.getItem('admin_adminFilter');
            const storedRole = sessionStorage.getItem('admin_roleFilter');
            
            if (storedCompany) {
                const parsed = JSON.parse(storedCompany);
                if (Array.isArray(parsed)) setCompanyFilter(parsed);
            }
            if (storedAdmin) {
                const parsed = JSON.parse(storedAdmin);
                if (Array.isArray(parsed)) setAdminFilter(parsed);
            }
            if (storedRole) {
                setRoleFilter(storedRole);
            }
        } catch (e) {
            // ignore malformed session data
            // eslint-disable-next-line no-console
            console.warn('Failed to load persisted admin filters', e);
        }
    }, []);

    // Persist filters to sessionStorage whenever they change
    useEffect(() => {
        try {
            sessionStorage.setItem('admin_companyFilter', JSON.stringify(companyFilter || []));
            sessionStorage.setItem('admin_adminFilter', JSON.stringify(adminFilter || []));
            sessionStorage.setItem('admin_roleFilter', roleFilter || 'all');
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Failed to persist admin filters', e);
        }
    }, [companyFilter, adminFilter, roleFilter]);

    // Persist UI preferences to sessionStorage whenever they change
    useEffect(() => {
        try {
            sessionStorage.setItem('admin_rowsPerPage', String(rowsPerPage));
            sessionStorage.setItem('admin_showCreatedColumn', String(showCreatedColumn));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Failed to persist admin UI preferences', e);
        }
    }, [rowsPerPage, showCreatedColumn]);
    // created column sort: null | 'asc' | 'desc'
    const [createdSort, setCreatedSort] = useState(null);
    // date passed sort: null | 'asc' | 'desc'
    const [datePassedSort, setDatePassedSort] = useState(null);
    // CSV import
    const [showCSVModal, setShowCSVModal] = useState(false);
    const [csvFile, setCSVFile] = useState(null);
    const [csvImportResults, setCSVImportResults] = useState(null);
    const [csvImporting, setCSVImporting] = useState(false);
    // Email Analytics
    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [selectedAnalyticsUser, setSelectedAnalyticsUser] = useState(null);
    // PIN codes admin view (superadmin only)
    const [showPinModal, setShowPinModal] = useState(false);
    const [pinCodes, setPinCodes] = useState([]);
    const [pinLoading, setPinLoading] = useState(false);
    const [pinPage, setPinPage] = useState(1);
    const [pinLimit, setPinLimit] = useState(200);
    const [pinTotal, setPinTotal] = useState(0);
    const [expandedPinId, setExpandedPinId] = useState(null);
    // Notification preferences
    const [showNotificationPrefsModal, setShowNotificationPrefsModal] = useState(false);
    const [notificationPrefs, setNotificationPrefs] = useState({
        pinRequests: true,
        errors: true,
        pageVisits: true,
        pdfClicks: true
    });
    const [prefsLoading, setPrefsLoading] = useState(false);
    const [showNotificationsPage, setShowNotificationsPage] = useState(false);
   
    const TEXT_TRANSLATORS = useMemo(() => (
    Array.isArray(users)
        ? users
            .filter(u =>
            u &&
            u.adminAuthorities &&
            (
                (u.adminAuthorities.translator && u.adminAuthorities.translator.enabled) ||
                (u.adminAuthorities.edit && u.adminAuthorities.edit.enabled) ||
                (u.adminAuthorities.uniqueEdit && u.adminAuthorities.uniqueEdit.enabled)
            )
            )
            .map(u => {
            if (!u.code) return null; // skip users without code
            const entry = {};
            entry[u.code] = (u.adminAuthorities.translator && Array.isArray(u.adminAuthorities.translator.pairs))
                ? u.adminAuthorities.translator.pairs
                : [];
            if (u.adminAuthorities.edit && Array.isArray(u.adminAuthorities.edit.languages)) {
                entry.edit = u.adminAuthorities.edit.languages;
            }
            if (u.adminAuthorities.uniqueEdit && Array.isArray(u.adminAuthorities.uniqueEdit.languages)) {
                entry.uniqueEdit = u.adminAuthorities.uniqueEdit.languages;
            }
            return entry;
            })
            .filter(Boolean) // remove nulls
        : []
    ), [users]);

    const getUserTranslationPairs = (userCode) => {
    const foundUser = Array.isArray(users)
        ? users.find(u => u && String(u.code) === String(userCode))
        : null;
    return foundUser &&
        foundUser.adminAuthorities &&
        foundUser.adminAuthorities.translator &&
        Array.isArray(foundUser.adminAuthorities.translator.pairs)
        ? foundUser.adminAuthorities.translator.pairs
        : [];
    };

    useEffect(() => {
        if (showModal && modalRef.current) {
            setTimeout(() => {
                modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
        }
        else if (editModal && editModalRef.current) {
            setTimeout(() => {
                editModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200); 
        }
    }, [showModal, editModal]);

    const goToTestReview = () => {
        const storedCode = sessionStorage.getItem('code');
        const activeCode = storedCode || code;
        const currentAdminUser = Array.isArray(users)
            ? users.find(u => String(u?.code) === String(activeCode))
            : null;

        const editLanguages = new Set();
        const translationLanguages = new Set();
        const translator = TEXT_TRANSLATORS.find(t => t.hasOwnProperty(String(activeCode)));
        if (Array.isArray(translator?.edit)) {
            translator.edit.forEach((lang) => editLanguages.add(String(lang).toLowerCase().trim()));
        }
        if (Array.isArray(currentAdminUser?.adminAuthorities?.edit?.languages)) {
            currentAdminUser.adminAuthorities.edit.languages.forEach((lang) => editLanguages.add(String(lang).toLowerCase().trim()));
        }
        if (Array.isArray(currentAdminUser?.adminAuthorities?.uniqueEdit?.languages)) {
            currentAdminUser.adminAuthorities.uniqueEdit.languages.forEach((lang) => editLanguages.add(String(lang).toLowerCase().trim()));
        }
        if (Array.isArray(currentAdminUser?.adminAuthorities?.translator?.pairs)) {
            currentAdminUser.adminAuthorities.translator.pairs.forEach((pair) => {
                if (pair?.from) translationLanguages.add(String(pair.from).toLowerCase().trim());
                if (pair?.to) translationLanguages.add(String(pair.to).toLowerCase().trim());
            });
        }

        if (currentAdminUser?.adminAuthorities) {
            sessionStorage.setItem('adminAuthorities', JSON.stringify(currentAdminUser.adminAuthorities));
            sessionStorage.setItem('testReview_adminAuthorities', JSON.stringify(currentAdminUser.adminAuthorities));
        }
        sessionStorage.setItem('testReview_editLanguages', JSON.stringify(Array.from(editLanguages)));
        sessionStorage.setItem('testReview_translationLanguages', JSON.stringify(Array.from(translationLanguages)));

        setOpen(false);
        history.push('/test-review');
    }
    const getTranslatorUsers = () => {
        const translatorCodes = TEXT_TRANSLATORS.map(translator => {
            const keys = Object.keys(translator); 
            return keys.find(key => key !== 'edit' && key !== 'uniqueEdit');
        });
        
        const translators = users.filter(user => 
            translatorCodes.includes(String(user.code))
        ).map(user => {
            const translator = TEXT_TRANSLATORS.find(t => t.hasOwnProperty(String(user.code)));
            return {
                ...user,
                editLanguages: translator ? translator.edit : [],
                translationPairs: translator ? translator[String(user.code)] : []
            };
        });
        
        setTranslatorUsers(translators);
        setShowUserSelection(true);
        setOpen(false);
    };

    const getUserToLanguages = useCallback((userCode) => {
        const translator = TEXT_TRANSLATORS.find(t => t.hasOwnProperty(String(userCode)));
        if (!translator) return [];
        
        // For users with edit property, use it
        if (translator.edit) {
            return translator.edit;
        }
        
        // For users without edit property (like 1000), extract from translation pairs
        const pairs = translator[String(userCode)];
        if (pairs && Array.isArray(pairs)) {
            return [...new Set(pairs.map(pair => pair.to))];
        }
        
        return [];
    }, [TEXT_TRANSLATORS]);

    function getManagerEmail(managerId) {
        console.log("Fetching email for manager ID:", managerId,users);
        const manager = users.find(u => u.code === managerId);
        return manager ? manager.email : '';
    }
    const getTranslationChanges = async (lang = 'fr', userId = null) => {
        setTranslationLoading(true);
        setShowUserSelection(false);
        
        try {
            let updateUrl;
            if (window.location.hostname === "localhost") {
                updateUrl = "http://localhost/scope/frontend/public/compare-translations.php";
            } else {
                updateUrl = "/compare-translations.php?t=" + Date.now();
            }

            const response = await fetch(updateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ lang, userId })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to fetch translation changes');
            }

            if (data.error) {
                throw new Error(data.error);
            }

            setTranslationChanges(data);
            setShowTranslationChanges(true);
        } catch (error) {
            console.error('Error fetching translation changes:', error);
            alert('Error fetching translation changes: ' + error.message);
        } finally {
            setTranslationLoading(false);
        }
    };

    const getUserLanguagePairs = (userCode) => {
        const translator = TEXT_TRANSLATORS.find(t => t.hasOwnProperty(String(userCode)));
        return translator ? translator[String(userCode)] : null;
    };

    const isMultiLanguageTranslator = (userCode) => {
        const languagePairs = getUserLanguagePairs(userCode);
        return languagePairs && languagePairs.length > 1;
    };

    const deleteSelectedUsers = () => {
        if (selectedUsers.length === 0) {
            alert("No users selected.");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)?`)) {
            return;
        }
        Promise.all(
            selectedUsers.map(id => deleteUser(id))
        ).then(() => {
            setUsers(users.filter(u => !selectedUsers.includes(u._id)));
            setSelectedUsers([]);
            setSelectedModal(false);
        }).catch(err => {
            console.log(err);
            alert("Failed to delete some users.");
        });
    };
    
    useEffect(() => {
        if (isSuperAdmin && selectedUser && selectedUser !== 'all') {
            const availableLanguages = getUserToLanguages(selectedUser);
            if (availableLanguages.length > 0) {
                setSelectedLang(availableLanguages[0]);
            }
        } else if (!isSuperAdmin && isTranslator) {
            const availableLanguages = getUserToLanguages(code);
            if (availableLanguages.length > 0) {
                setSelectedLang(availableLanguages[0]);
            }
        }
    }, [selectedUser, isSuperAdmin, isTranslator, code, getUserToLanguages, users]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (setOpen && !event.target.closest('.Admin-Navbar-buttons-container')) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setOpen]);

    // remove DOM search helper (we'll use controlled searchTerm)

    useEffect(() => {
            const storedCode = sessionStorage.getItem("code");
            if (storedCode) {
                setCode(storedCode);
                setCanAddTests(CAN_ADD_USERS.includes(Number(storedCode)));
            
            const translatorCodes = TEXT_TRANSLATORS.map(translator => {
                const keys = Object.keys(translator);
                return keys.find(key => key !== 'edit' && key !== 'uniqueEdit');
            }).filter(code => code !== undefined);
            
            setIsTranslator(translatorCodes.includes(storedCode));

            Axios.post(url.allUsers, { code: storedCode }, {
             headers: {
                            "x-access-token": (() => {
                                const token = sessionStorage.getItem("token");
                                if (!token || token === "null" || token === "undefined") {
                                    return null;
                                }
                                try {
                                    return JSON.parse(token);
                                } catch (e) {
                                    return null;
                                }
                            })()
                    }
            }).then(res => {
                if (res.data.auth === false) {
                    sessionStorage.clear();
                    window.location.reload();
                } else {
                    const reordered = res.data.users.sort((a,b) => a.code - b.code);
                    const currentAdminUser = reordered.find(u => String(u.code) === String(storedCode));
                    //console.log("Current admin user:", currentAdminUser, storedCode);
                    setUsers(reordered);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                    //console.log("User loaded:", currentAdminUser.company.length, res.data);
                    setIsSuperAdmin(res.data.isSuperAdmin);
                    const hasEditRights = Boolean(
                        currentAdminUser?.adminAuthorities?.edit?.enabled ||
                        currentAdminUser?.adminAuthorities?.uniqueEdit?.enabled
                    );
                    setCanReviewQuestionnaire(Boolean(res.data.isSuperAdmin || hasEditRights));
                    if(res.data.isSuperAdmin || currentAdminUser.company.length > 1){
                        Axios.post(url.getCompanies, { code: storedCode }, {
                            headers: {
                                "x-access-token": (() => {
                                    const token = sessionStorage.getItem("token");
                                    if (!token || token === "null" || token === "undefined") {
                                        return null;
                                    }
                                    try {
                                        return JSON.parse(token);
                                    } catch (e) {
                                        return null;
                                    }
                                })()
                            }
                        })
                        .then(comps => {
                            let filteredCompanies = [];
                            if (Array.isArray(comps.data)) {
                                filteredCompanies = comps.data;
                                if (!res.data.isSuperAdmin) {
                                    filteredCompanies = comps.data.filter(
                                        c => Array.isArray(currentAdminUser.company) && currentAdminUser.company.includes(c._id)
                                    );
                                }
                            }
                            setCompanies(filteredCompanies);
                        })
                        .catch(err => {
                            console.log(err);
                            setCompanies([]); // fallback to empty array on error
                        });
                    }
                }
            })
            .catch(err => {
                console.log(err)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // API Keys management (admin-only)
    const fetchApiKeys = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('token'));
            const res = await Axios.get(url.base + '/apikeys', { headers: { 'x-access-token': token } });
            setApiKeys(res.data || []);
        } catch (e) {
            console.error('Failed to fetch API keys', e);
        }
    };

    const handleCreateKey = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('token'));
            const res = await Axios.post(url.base + '/apikeys', { owner: newKeyOwner, description: newKeyDesc }, { headers: { 'x-access-token': token } });
            if (res.data && res.data.key) {
                alert('Key created: ' + res.data.key);
                setNewKeyOwner(''); setNewKeyDesc('');
                fetchApiKeys();
            }
        } catch (e) {
            console.error('Create key failed', e);
            alert('Create key failed');
        }
    };

    const handleRevokeKey = async (key) => {
        if (!window.confirm('Revoke this key?')) return;
        try {
            const token = JSON.parse(sessionStorage.getItem('token'));
            await Axios.post(url.base + '/apikeys/revoke/' + encodeURIComponent(key), {}, { headers: { 'x-access-token': token } });
            fetchApiKeys();
        } catch (e) {
            console.error('Revoke failed', e);
            alert('Revoke failed');
        }
    };

    const fetchNotificationPrefs = async () => {
        try {
            setPrefsLoading(true);
            const token = JSON.parse(sessionStorage.getItem('token'));
            const res = await Axios.get(url.base + '/push/preferences', { 
                headers: { 'x-access-token': token } 
            });
            if (res.data && res.data.preferences) {
                setNotificationPrefs(res.data.preferences);
            }
        } catch (e) {
            console.error('Failed to fetch notification preferences', e);
        } finally {
            setPrefsLoading(false);
        }
    };

    const handleUpdateNotificationPrefs = async () => {
        try {
            setPrefsLoading(true);
            const token = JSON.parse(sessionStorage.getItem('token'));
            await Axios.post(url.base + '/push/preferences', 
                { preferences: notificationPrefs }, 
                { headers: { 'x-access-token': token } }
            );
            alert('Notification preferences updated successfully!');
        } catch (e) {
            console.error('Failed to update notification preferences', e);
            alert('Failed to update notification preferences');
        } finally {
            setPrefsLoading(false);
        }
    };

    // Derived filtered users for the table (applies company/admin/role filters)
    const filteredUsers = useMemo(() => {
        if (!Array.isArray(users)) return [];
        const baseFiltered = users.filter(user => {
            if (!user) return false;

            // Role filter (only-admins / only-users / only-promo)
            if (roleFilter === 'only-admins' && String(user.authorities) !== 'admin') return false;
            if (roleFilter === 'only-users' && String(user.authorities) !== 'user') return false;
            if (roleFilter === 'only-promo' && String(user.authorities) !== 'promo') return false;

            // Search term filter (case-insensitive) - match email, code, key, company name
            if (searchTerm && String(searchTerm).trim() !== '') {
                const s = String(searchTerm).toLowerCase();
                const email = (user.email || '').toLowerCase();
                const code = (user.code || '').toString().toLowerCase();
                const key = (user.key || '').toLowerCase();
                const companyNames = Array.isArray(user.company)
                    ? user.company.map(cid => (companies.find(c => String(c._id) === String(cid)) || {}).name || '').join(' ').toLowerCase()
                    : ((companies.find(c => String(c._id) === String(user.company)) || {}).name || '').toLowerCase();
                if (!(email.includes(s) || code.includes(s) || key.includes(s) || companyNames.includes(s))) return false;
            }

            // Company filter: if companyFilter array has items, require intersection
            if (Array.isArray(companyFilter) && companyFilter.length > 0) {
                try {
                    const userCompanies = Array.isArray(user.company) ? user.company : [user.company];
                    const has = userCompanies.some(uc => companyFilter.some(cf => String(cf) === String(uc)));
                    if (!has) return false;
                } catch (e) {
                    return false;
                }
            }

            // Admin filter: if adminFilter array has items, require that user shares a company with at least one selected admin
            if (Array.isArray(adminFilter) && adminFilter.length > 0) {
                const selectedAdmins = users.filter(u => u && adminFilter.some(aid => String(aid) === String(u._id)));
                if (selectedAdmins.length === 0) return false;
                const adminCompanies = selectedAdmins.reduce((acc, a) => {
                    const comps = Array.isArray(a.company) ? a.company : [a.company];
                    comps.forEach(c => { if (c !== undefined && c !== null) acc.add(String(c)); });
                    return acc;
                }, new Set());
                try {
                    const userCompanies = Array.isArray(user.company) ? user.company : [user.company];
                    const intersect = userCompanies.some(uc => adminCompanies.has(String(uc)));
                    if (!intersect) return false;
                } catch (e) {
                    return false;
                }
            }

            return true;
        });

        const deduped = [];
        const seen = new Set();
        baseFiltered.forEach((u, index) => {
            const key = String(
                (u && (u.code ?? u._id ?? u.email)) || `idx-${index}`
            );
            if (seen.has(key)) return;
            seen.add(key);
            deduped.push(u);
        });

        const activeCode = String(sessionStorage.getItem('code') || code || '').trim();
        if (!activeCode) {
            return deduped;
        }

        const currentAdminIndex = deduped.findIndex((u) => String(u?.code) === activeCode);
        if (currentAdminIndex > 0) {
            const [currentAdminUser] = deduped.splice(currentAdminIndex, 1);
            deduped.unshift(currentAdminUser);
        }

        return deduped;
    }, [users, companyFilter, adminFilter, roleFilter, companies, searchTerm, code]);

    // Pagination: compute displayedUsers from filteredUsers
    const totalFiltered = Array.isArray(filteredUsers) ? filteredUsers.length : 0;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / (rowsPerPage || 1)));
    // Persist created column preference
    useEffect(() => {
        try {
            const stored = localStorage.getItem('admin_showCreatedColumn');
            if (stored !== null) setShowCreatedColumn(stored === 'true');
        } catch (e) {
            // ignore
        }
    }, []);
    useEffect(() => {
        try { localStorage.setItem('admin_showCreatedColumn', showCreatedColumn ? 'true' : 'false'); } catch (e) {}
    }, [showCreatedColumn]);

    // Apply sorting when requested: dateSurveyPassed (Date Passed) takes precedence, otherwise Created
    const sortedFilteredUsers = useMemo(() => {
        if (!Array.isArray(filteredUsers)) return [];

        // If date passed sort is active, sort by dateSurveyPassed
        if (datePassedSort) {
            const copy = [...filteredUsers];
            const getTimeOrNull = (u) => {
                if (!u || u.testPassed !== true) return null;
                const t = u.dateSurveyPassed ? new Date(u.dateSurveyPassed).getTime() : NaN;
                return Number.isFinite(t) ? t : null;
            };
            copy.sort((a, b) => {
                const da = getTimeOrNull(a);
                const db = getTimeOrNull(b);
                // Place null (invalid/missing) values at the end regardless of sort direction
                if (da === null && db === null) return 0;
                if (da === null) return 1;
                if (db === null) return -1;
                return datePassedSort === 'asc' ? da - db : db - da;
            });
            return copy;
        }

        // Fallback to createdSort
        if (createdSort) {
            const copy = [...filteredUsers];
            copy.sort((a, b) => {
                const da = a && a.dateUserAdded ? new Date(a.dateUserAdded).getTime() : 0;
                const db = b && b.dateUserAdded ? new Date(b.dateUserAdded).getTime() : 0;
                return createdSort === 'asc' ? da - db : db - da;
            });
            return copy;
        }

        return filteredUsers;
    }, [filteredUsers, createdSort, datePassedSort]);

    const displayedUsers = useMemo(() => {
        if (!Array.isArray(sortedFilteredUsers)) return [];
        if (rowsPerPage === 'all' || rowsPerPage === 0) return sortedFilteredUsers;
        const start = (currentPage - 1) * rowsPerPage;
        return sortedFilteredUsers.slice(start, start + rowsPerPage);
    }, [sortedFilteredUsers, currentPage, rowsPerPage]);

    // Stats based on active filteredUsers
    const stats = useMemo(() => {
        const base = Array.isArray(filteredUsers) ? filteredUsers : [];
        return {
            total: base.length,
            admins: base.filter(u => String(u.authorities) === 'admin').length,
            users: base.filter(u => String(u.authorities) === 'user').length,
            promo: base.filter(u => String(u.authorities) === 'promo').length,
            completed: base.filter(u => u && u.testPassed === true).length,
            notCompleted: base.filter(u => u && u.testPassed !== true).length,
        };
    }, [filteredUsers]);

// controlled searchTerm replaces the old DOM-based search

    const downloadUsersCSV = () => {
        const softskillsIDs = ["Ext", "Exc", "Imp", "Pes", "Sas", "Dom", "Agg", "Con", "Cap", "Scm", "Pro", "Emo", "Sem", "Rum", "Iat", "Sss", "Wis", "Scb", "Exp", "Are", "Str", "Neg", "Irr", "Ser", "Anx", "Sco", "Enj", "Lea", "Sec", "Ist", "Sal", "Tas", "Pow", "Suc", "Mor", "Int", "Aff", "Har"];
        let text = "";
        text += "Code; Survey passed; Date passed; Company; Language; Type";
        
        if (users.find(el => el.testPassed === true)) {
            softskillsIDs.forEach(soft => {
                text += ";" + soft;
            })
        }

        text += "\r";
        for (let i = 0; i < users.length; i++) {
            text += (users[i].code) + ";";
            text += (users[i].testPassed) + ";";
            text += (users[i].dateSurveyPassed) + ";";
            text += "skillspotting;";
            text += (users[i].language) + ";";
            if (users[i].testPassed === true) {
                text += (users[i].answers[101]["POSTE"] + users[i].answers[97]["SEXE"]);
            } else {
                text += "No data yet";
            }

            if (users[i].testPassed === false) {
                for (let k = 0; k < users[i].results.length; k++) {
                    text += "noData; ";
                }
            } else {
                for (let k = 0; k < users[i].results.length; k++) {
                    for (let z = 0; z < users[i].results[k].data.length; z++) {
                        let data = users[i].results[k].data[z].user;
                        text += ";" + data;
                    }
                }
            }
            text += "\r";
        }
        let txtContent = "data:text/csv;charset=utf-8," + encodeURIComponent(text);
        let anchor = document.createElement('a');
        anchor.href = "data:" + txtContent;
        anchor.target = '_blank';
        anchor.download = "allUserResults.csv";
        anchor.click();
        setOpen(false);
    }

    /*
    const downloadUserReportPDF = (code, status, dateSurveyPassed, results, answers) => {
        alert("not functional yet");
    };
    */
   /*
    const downloadUserCSV = (code, status, dateSurveyPassed, results, answers) => {
        let date = new Date(Date.parse(dateSurveyPassed)).toDateString();
        let text = "";
        text += "Code; Survey passed; Date passed; Type";
        if (results.length === undefined) {
            alert("Ces users n'ont pas de données")
        } else {
            for (let k = 0; k < results.length; k++) {
                for (let i = 0; i < results[k].data.length; i++) {
                    let title = results[k].data[i].fr;
                    text += ";" + title.slice(0, 3)
                }
            }
            text += "\r";
            text += code + ";" + status + ";" + date + ";";
            if (status === true) {
                text += (answers[101]["POSTE"] + answers[97]["SEXE"]);
            } else {
                text += "No data yet";
            }
            for (let k = 0; k < results.length; k++) {
                for (let i = 0; i < results[k].data.length; i++) {
                    let data = results[k].data[i].user;
                    text += ";" + data;
                }
            }
        }

        let txtContent = "data:text/csv;charset=utf-8," + encodeURIComponent(text);
        let anchor = document.createElement('a');
        anchor.href = "data:" + txtContent;
        anchor.target = '_blank';
        anchor.download = "user_" + code + ".csv";
        anchor.click();
    }
    */
    const sendMailUser = (code, email, key) => {
        let keyCrypted = key + "NaSa";
        if (email !== "") {
            Axios.post(url.sendEmail, { emailReceiver: email, keyReceiver: keyCrypted }, {
                headers: { "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null") }
            })
            .then(res => {
                let btn = document.getElementById(code);
                btn.firstChild.data = "Email already sent";
                btn.disabled = true;
            })
            .catch(err => {
                console.log(err)
            })
        } else {
            alert("Email already sent, recharge page");
        }
    }

    const signOut = () => {
        setTimeout(() => {
            sessionStorage.clear();
            window.location.reload()
        }, 1000);
    }

    const toggleAdminPermissions = () => {
        setOpenAdminPermissions(openAdminPermissions => !openAdminPermissions);
    };

    const deleteUser = (id) => {
        Axios.post(url.deleteUser, {id: id, code: sessionStorage.getItem("code") }, {
            headers: { "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null") }
        })
        .then(res => {
            if(res.data.message === "success"){
                //window.location.reload();
            } else {
                console.log("error", res);
            }
        })
        .catch(err => {
            console.log(err)
        })
    }

    const archiveUser = (id) => {
        alert("archiveUser coming soon");
    }

    const sendData2User = (id) => {
        alert("sendData2User coming soon");
    }

    const handleCompanyAdmins = (companyId) => {
        let companyUsers = users.filter(
            u => String(u.authorities) === "admin" &&
            Array.isArray(u.company) &&
            u.company.includes(companyId)
        );
        //console.log("company USERS", companyUsers.length,companyUsers);
        return companyUsers.length > 0 ? companyUsers : [{_id: 'none', email: 'No admin for this company'}];
    }

    const handleChange = (e) => {
        //console.log("handleChange a ", e.target.name, e.target.value,users);
        if(e.target.name === 'company'){
            handleCompanyAdmins(e.target.value);
        }
        const newUser = { ...user }
        newUser[e.target.name] = e.target.value
        setUser(newUser);
    }

const updateUser = (e) => {
    const target = e.target || e;
    if (!target || !target.name) {
        console.warn("updateUser called without a valid target:", e);
        return;
    }
    const newUser = { ...user };
    console.log("updateUser", target.name, target.value, newUser);

    // Handle authorities and adminAuthorities correctly
    if (target.name === 'authorities') {
        newUser.authorities = target.value;
        if (target.value === 'admin') {
            // Only set adminAuthorities to an object if admin
            newUser.adminAuthorities = {
                contentEditor: false,
                translator: { enabled: false, pairs: [] },
                edit: { enabled: false, languages: [] },
                uniqueEdit: { enabled: false, languages: [] }
            };
        } else {
            newUser.adminAuthorities = false;
        }
    } else if (target.name === 'adminAuthorities') {
        // Always expect an object here
        newUser.adminAuthorities = target.value;
    } else {
        newUser[target.name] = target.value;
    }
    setUser(newUser);

    // Build update payload
    const updateData = {
        id: newUser._id,
        code: sessionStorage.getItem("code")
    };

    if (target.name === 'authorities') {
        updateData.authorities = newUser.authorities;
        if (newUser.authorities === 'admin') {
            updateData.adminAuthorities = newUser.adminAuthorities;
        }
    } else if (target.name === 'adminAuthorities') {
    updateData.adminAuthorities = newUser.adminAuthorities;
    // Also send authorities if present
    if (target.authorities) {
        updateData.authorities = target.authorities;
    }
} else if (target.name === 'company') {
        updateData.company = newUser.company;
        console.log("Updated company:", newUser.company);
    } else if (target.name === 'email') {
        updateData.email = newUser.email;
    } else if (target.name === 'bccEmail') {
        updateData.bccEmail = newUser.bccEmail;
    } else if (target.name === 'roleLabel') {
        updateData.roleLabel = newUser.roleLabel;
    } else if (target.name === 'emailTemplate') {
        updateData.emailTemplate = newUser.emailTemplate;
    } else if (target.name === 'language') {
        updateData.language = newUser.language;
    }

    Axios.post(url.updateUser, updateData, {
        headers: { "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null") }
    })
    .then(res => {
        if(res.data.success){
            const updatedUsers = users.map(u => 
                u._id === newUser._id ? { ...u, [target.name]: newUser[target.name] } : u
            );
            setUsers(updatedUsers);
        } else {
            alert(res.data.msg || res.data.message)
        }
    })
    .catch(err => {
        console.log(err);
        alert('Update failed');
    });
}

    const handleSubmit = (e) => {
        console.log("handleSubmit", "quantity", licence, "email", user.email, "language",user.language, "companyId", user.company, "authorities", user.authorities,"administrator", user.admin, "code", sessionStorage.getItem("code"));
        e.preventDefault();

        Axios.post(url.addUser, {quantity: licence, email: user.email, language: user.language, companyId: user.company, authorities: user.authorities,administrator: user.admin, code: sessionStorage.getItem("code") }, {
            headers: { "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null") }
        })
        .then(res => {
            if(res.data.success){
                const newUsers = [...users];
                newUsers.push(res.data.user)
                alert('ok user added !');
                setShowModal(false);
                setUser({});
                setUsers(newUsers);
                window.location.reload();
            } else {
                alert(res.data.msg)
            }
        })
        .catch(err => {
            console.log(err)
        })
    }

    const addUser = () => {
        setShowModal(true);
        setEditMode(false);

        if (isSuperAdmin) {
            setUser({
                language: 'fr',
                authorities: 'user',
                company: companies.length > 0 ? companies[0]._id : ''
            });
        } else {
            setUser({ language: 'fr', authorities: 'user' });
        }
    }

    const handleSubmitEdit = (e) => {
        e.preventDefault();
    }

    const editUser = (user) => {
        setUser(user);
        setEditModal(true);
        setEditMode(false); 
    }

    const editOptions = (user) => {
        setUser(user);
        setSelectedModal(true);
        setSelectedUsers(prevSelected => {
            if (prevSelected.includes(user._id)) {
                return prevSelected.filter(id => id !== user._id);
            } else {
                return [...prevSelected, user._id];
            }
        });
    };

    const handleCSVFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCSVFile(file);
            setCSVImportResults(null); // Clear previous results
        }
    };

    const handleCSVImport = async () => {
        if (!csvFile) {
            alert('Please select a CSV file first');
            return;
        }

        setCSVImporting(true);
        setCSVImportResults(null);

        try {
            // Read CSV file
            const text = await csvFile.text();
            const lines = text.split('\n').filter(line => line.trim() !== '');
            
            if (lines.length < 1) {
                alert('CSV file is empty');
                setCSVImporting(false);
                return;
            }

            // Auto-detect delimiter: check first line for |, comma, or tab
            const firstLine = lines[0];
            let delimiter = ','; // default
            if (firstLine.includes('|')) {
                delimiter = '|';
            } else if (firstLine.includes('\t')) {
                delimiter = '\t';
            }

            // Detect if first line is a header by checking if it contains common header keywords
            const firstLineLower = firstLine.toLowerCase();
            const hasHeader = firstLineLower.includes('admin') && 
                             firstLineLower.includes('email') && 
                             firstLineLower.includes('company');
            
            const startIndex = hasHeader ? 1 : 0; // Skip first line only if it's a header

            // Parse CSV: admin|email|bcc|company|roleLabel|language|emailTemplate (supports pipe, comma, or tab delimiters)
            const csvData = [];
            for (let i = startIndex; i < lines.length; i++) {
                const parts = lines[i].split(delimiter).map(p => p.trim());
                if (parts.length >= 4) { // Minimum required fields
                    csvData.push({
                        admin: parts[0] || '',
                        email: parts[1] || '',
                        bcc: parts[2] || '', // Optional
                        company: parts[3] || '',
                        roleLabel: parts[4] || '', // Optional: 'coach', 'HR manager', or 'mentor'
                        language: parts[5] || parts[4] || 'en', // Language at position 5, fallback to position 4 for backward compatibility
                        emailTemplate: parts[6] || '' // Optional: 'standard' or 'deadline'
                    });
                }
            }

            if (csvData.length === 0) {
                alert('No valid rows found in CSV');
                setCSVImporting(false);
                return;
            }

            // Send to backend
            const response = await Axios.post(
                url.importUsersCSV,
                { csvData },
                {
                    headers: {
                        "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null")
                    }
                }
            );

            setCSVImportResults(response.data.results);
            setCSVImporting(false);

            // Refresh users list if any were created
            if (response.data.summary.created > 0) {
                // Reload users
                const storedCode = sessionStorage.getItem("code");
                const usersResponse = await Axios.post(url.allUsers, { code: storedCode }, {
                    headers: {
                        "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null")
                    }
                });
                if (usersResponse.data.users) {
                    setUsers(usersResponse.data.users);
                }
            }
        } catch (err) {
            console.error('CSV import error:', err);
            alert('Failed to import CSV: ' + (err.response?.data?.message || err.message));
            setCSVImporting(false);
        }
    };

    const fetchUserAnalytics = async (userEmail) => {
        setAnalyticsLoading(true);
        setSelectedAnalyticsUser(userEmail);
        try {
            const response = await Axios.get(
                `${url.getUserAnalytics}${userEmail}`,
                {
                    headers: {
                        "x-access-token": JSON.parse(sessionStorage.getItem("token") || "null")
                    }
                }
            );
            
            if (response.data.success) {
                setAnalyticsData(response.data.analytics);
                setShowAnalyticsModal(true);
            } else {
                alert('Failed to fetch analytics');
            }
            setAnalyticsLoading(false);
        } catch (err) {
            console.error('Analytics fetch error:', err);
            alert('Failed to fetch analytics: ' + (err.response?.data?.message || err.message));
            setAnalyticsLoading(false);
        }
    };

    const fetchPinCodes = async (page = 1, limit = 200) => {
        setPinLoading(true);
        try {
            const token = JSON.parse(sessionStorage.getItem('token') || 'null');
            const response = await Axios.get(`${url.base}/admin/pincodes`, {
                params: { page, limit },
                headers: { 'x-access-token': token }
            });

            if (response.data && response.data.success) {
                setPinCodes(response.data.items || []);
                setPinPage(response.data.page || page);
                setPinLimit(response.data.limit || limit);
                setPinTotal(response.data.total || 0);
                setShowPinModal(true);
            } else {
                alert('Failed to fetch PIN codes');
            }
        } catch (err) {
            console.error('Failed to fetch PIN codes', err);
            alert('Failed to fetch PIN codes: ' + (err.response?.data?.message || err.message));
        } finally {
            setPinLoading(false);
        }
    };

    const toggleExpandPin = (id) => {
        setExpandedPinId(prev => prev === id ? null : id);
    }

    const copyToClipboard = (text) => {
        if (!text) return;
        try {
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text);
                return;
            }
        } catch (e) {
            // ignore and fallback
        }
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) { }
        document.body.removeChild(ta);
    }

    const prevPinPage = () => {
        if (pinPage > 1) fetchPinCodes(pinPage - 1, pinLimit);
    }

    const nextPinPage = () => {
        const maxPage = Math.ceil(pinTotal / pinLimit) || 1;
        if (pinPage < maxPage) fetchPinCodes(pinPage + 1, pinLimit);
    }

    // Prevent page scrolling when PIN modal is open
    useEffect(() => {
        if (showPinModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        // cleanup when component unmounts
        return () => document.body.classList.remove('modal-open');
    }, [showPinModal]);

    const closeEditUser = (user) => {
        setUser({});
        setEditModal(false);
        setEditMode(false); 
    }

    const closeModal = () => {
        setUser({});
        setShowModal(false);
        setEditMode(false);
    }

    const closePreferences = () => {
        setOpen(o => !o);
        console.log("Admin menu toggled:", !open);
    };

    return (
        <div>
            {loading ? '' : 
                <div className="Admin-Navbar">                         

                    <input className="Admin-searchBar" id="Admin-searchBar" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} type="text" placeholder="Search email, key, status, code, company..."></input>
                    {/* Attitudes Button */}

                    <div className='Admin-Navbar-buttons-container'>
                        <div>
                            <svg version="1.1" className={`Preferences-menu white-menu ${open ? ' open' : ''}`} xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20px" height="20px" viewBox="0 0 124 124" onClick={() => closePreferences()}>
                            <g>
                                <path d="M112,6H12C5.4,6,0,11.4,0,18s5.4,12,12,12h100c6.6,0,12-5.4,12-12S118.6,6,112,6z" />
                                <path d="M112,50H12C5.4,50,0,55.4,0,62c0,6.6,5.4,12,12,12h100c6.6,0,12-5.4,12-12C124,55.4,118.6,50,112,50z" />
                                <path d="M112,94H12c-6.6,0-12,5.4-12,12s5.4,12,12,12h100c6.6,0,12-5.4,12-12S118.6,94,112,94z" />
                            </g>
                        </svg>
                        </div>

                        <div className={`Admin-Navbar-buttons ${open ? 'show' : ''}`}>
                            {isSuperAdmin && 
                                <div className="Admin-NavBar-btn" onClick={downloadUsersCSV}>
                                    <svg fill="white" width="30px" height="30px" viewBox="0 0 24 24">
                                        <g>
                                            <path d="M6 21H18M12 3V17M12 17L17 12M12 17L7 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </g>
                                    </svg>
                                    <span>Download All Users CSV</span>
                                </div>
                            }
                            {canReviewQuestionnaire && 
                                <div className="Admin-NavBar-btn" onClick={() => {
                                    goToTestReview();
                                }}>
                                    <svg fill="white" width="30px" height="30px" viewBox="0 0 24 24">
                                        <g>
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 17a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm1-5h-2v-1c0-1.1.9-2 2-2s2-.9 2-2-.9-2-2-2-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 1.65-1.35 3-3 3z"/>
                                        </g>
                                    </svg>
                                    <span>Review Questionnaire</span> 
                                </div>
                            }
                            {(isTranslator || isSuperAdmin) && 
                                <div className="Admin-NavBar-btn" onClick={() => {
                                    if (isSuperAdmin) {
                                        getTranslatorUsers();
                                    } else if (isTranslator && isMultiLanguageTranslator(code)) {
                                        setShowUserSelection(true);
                                        setSelectedUser(code);
                                        setOpen(false);
                                    } else {
                                        getTranslationChanges('fr', code);
                                    }
                                }}>
                                    <svg fill="white" width="30px" height="30px" viewBox="0 0 24 24">
                                        <g>
                                            <path d="M3 12h18m-9-9l9 9-9 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </g>
                                    </svg>
                                    <span>Translation Changes</span> 
                                </div>
                            }
                            {isSuperAdmin && 
                                <>
                                <div className="Admin-NavBar-btn" onClick={() => { fetchPinCodes(1, pinLimit); setOpen(false); }}>
                                    <svg width="30px" height="30px" viewBox="0 0 24 24" fill="white" style={{marginLeft: '2px'}}>
                                        <g>
                                            <path d="M12 1C9.243 1 7 3.243 7 6v3H6a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3v3H9V6c0-1.654 1.346-3 3-3z" />
                                        </g>
                                    </svg>
                                    <span>Auth PINs</span>
                                </div>
                                <Link className="Admin-NavBar-btn" to={{ pathname: '/companyManager' }}>
                                    <svg width="30px" height="30px" viewBox="0 0 16 16">
                                        <rect width="16" height="16" fill="#ccc" opacity="0"/>
                                        <g transform="translate(0.648 -0.621)">
                                            <g transform="translate(2.352 2.621)" fill="none" stroke="white" strokeWidth="1">
                                                <path d="M1,0H9a1,1,0,0,1,1,1V12a0,0,0,0,1,0,0H0a0,0,0,0,1,0,0V1A1,1,0,0,1,1,0Z" stroke="none"/>
                                                <rect x="0.5" y="0.5" width="9" height="11" rx="0.5" fill="none"/>
                                            </g>
                                            <g transform="translate(5.352 9.621)" fill="none" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1">
                                                <rect width="4" height="5" stroke="none"/>
                                                <rect x="0.5" y="0.5" width="3" height="4" fill="none"/>
                                            </g>
                                            <g>
                                                <g transform="translate(5.352 5.621)" fill="none" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1">
                                                    <rect width="1" height="1" stroke="none"/>
                                                    <rect x="0.5" y="0.5" fill="none"/>
                                                </g>
                                                <g transform="translate(5.352 7.621)" fill="none" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1">
                                                    <rect width="1" height="1" stroke="none"/>
                                                    <rect x="0.5" y="0.5" fill="none"/>
                                                </g>
                                                <g transform="translate(8.352 5.621)" fill="none" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1">
                                                    <rect width="1" height="1" stroke="none"/>
                                                    <rect x="0.5" y="0.5" fill="none"/>
                                                </g>
                                                <g transform="translate(8.352 7.621)" fill="none" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1">
                                                    <rect width="1" height="1" stroke="none"/>
                                                    <rect x="0.5" y="0.5" fill="none"/>
                                                </g>
                                            </g>
                                        </g>
                                    </svg>
                                    <span>Manage company</span>
                                </Link>
                                <div className="Admin-NavBar-btn" onClick={() => { fetchApiKeys(); setShowApiKeysModal(true); setOpen(false); }}>
                                    <svg width="30px" height="30px" viewBox="0 0 24 24" style={{marginLeft: '4px'}} fill="white">
                                        <g>
                                            <path d="M12 1a4 4 0 00-4 4v1H6a2 2 0 00-2 2v6a2 2 0 002 2h2v1a4 4 0 004 4h0a4 4 0 004-4v-1h2a2 2 0 002-2v-6a2 2 0 00-2-2h-2V5a4 4 0 00-4-4zM9 6a3 3 0 116 0v1H9V6z"/>
                                        </g>
                                    </svg>
                                    <span>API Keys</span>
                                </div>
                                <Link className="Admin-NavBar-btn" to={{ pathname: '/analytics' }}>
                                    <svg width="30px" height="30px" viewBox="0 0 24 24" style={{marginLeft: '4px'}} fill="white">
                                        <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 12h2v-4H7v4zm4 0h2V9h-2v8zm4 0h2v-6h-2v6z"/>
                                    </svg>
                                    <span>Analytics</span>
                                </Link>
                                <div className="Admin-NavBar-btn" onClick={() => { fetchNotificationPrefs(); setShowNotificationsPage(true); setOpen(false); }}>
                                    <svg width="30px" height="30px" viewBox="0 0 24 24" style={{marginLeft: '4px'}} fill="white">
                                        <path d="M12 2C11.172 2 10.5 2.672 10.5 3.5V4.19C8.91 4.74 7.74 6.23 7.74 8V12L6 14V15H18V14L16.26 12V8C16.26 6.23 15.09 4.74 13.5 4.19V3.5C13.5 2.672 12.828 2 12 2ZM10 16C10 17.1 10.9 18 12 18C13.1 18 14 17.1 14 16H10Z"/>
                                    </svg>
                                    <span>Notifications</span>
                                </div>
                                </>
                            }
                            {/* Add Log out button here */}
                            <div className="Admin-NavBar-btn" onClick={() => setLogout(true)}>
                                <svg width="30px" height="30px" viewBox="0 0 384.971 384.971" style={{marginLeft: '4px'}} fill="white">
                                    <g>
                                        <g>
                                            <path d="M180.455,360.91H24.061V24.061h156.394c6.641,0,12.03-5.39,12.03-12.03s-5.39-12.03-12.03-12.03H12.03C5.39,0.001,0,5.39,0,12.031V372.94c0,6.641,5.39,12.03,12.03,12.03h168.424c6.641,0,12.03-5.39,12.03-12.03 C192.485,366.299,187.095,360.91,180.455,360.91z"/>
                                            <path d="M381.481,184.088l-83.009-84.2c-4.704-4.752-12.319-4.74-17.011,0c-4.704,4.74-4.704,12.439,0,17.179l62.558,63.46H96.279 c-6.641,0-12.03,5.438-12.03,12.151c0,6.713,5.39,12.151,12.03,12.151h247.74l-62.558,63.46c-4.704,4.752-4.704,12.439,0,17.179 c4.704,4.752,12.319,4.752,17.011,0l82.997-84.2C386.113,196.588,386.161,188.756,381.481,184.088z"/>
                                        </g>
                                    </g>
                                </svg>
                                <span>Log out</span>
                            </div>
                        </div>

                        {/* API Keys modal (super-admin only) */}
                        {isSuperAdmin && showApiKeysModal && (
                            <div className="Modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }} onClick={() => setShowApiKeysModal(false)}>
                                <div className="Admin-keys modal" style={{ width: '95%', maxWidth: 900, marginTop: 12, padding: 16, borderRadius: 8, background: '#fff' }} onClick={e => e.stopPropagation()}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h4 style={{ margin: 0 }}>API Keys</h4>
                                        <button onClick={() => setShowApiKeysModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
                                    </div>
                                    <div style={{ marginBottom: 8, marginTop: 8 }}>
                                        <input placeholder="Owner" value={newKeyOwner} onChange={e => setNewKeyOwner(e.target.value)} />
                                        <input placeholder="Description" value={newKeyDesc} onChange={e => setNewKeyDesc(e.target.value)} style={{ marginLeft: 8 }} />
                                        <button onClick={handleCreateKey} style={{ marginLeft: 8 }}>Create</button>
                                        <button onClick={() => fetchApiKeys()} style={{ marginLeft: 8 }}>Refresh</button>
                                    </div>
                                    <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th>Key</th>
                                                    <th>Owner</th>
                                                    <th>Description</th>
                                                    <th>Revoked</th>
                                                    <th>Created</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {apiKeys.map(k => (
                                                    <tr key={k.key}>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{k.key}</td>
                                                        <td>{k.owner}</td>
                                                        <td>{k.description}</td>
                                                        <td>{k.revoked ? 'Yes' : 'No'}</td>
                                                        <td>{new Date(k.createdAt).toLocaleString()}</td>
                                                        <td>{!k.revoked && <button onClick={() => handleRevokeKey(k.key)}>Revoke</button>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showNotificationsPage && (
                            <AdminNotifications onClose={() => setShowNotificationsPage(false)} />
                        )}

                        {/* Notification Preferences modal (super-admin only) */}
                        {isSuperAdmin && showNotificationPrefsModal && (
                            <div className="Modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }} onClick={() => setShowNotificationPrefsModal(false)}>
                                <div className="notification-prefs-modal" style={{ width: '95%', maxWidth: 500, padding: 24, borderRadius: 8, background: '#fff' }} onClick={e => e.stopPropagation()}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                        <h4 style={{ margin: 0 }}>Notification Preferences</h4>
                                        <button onClick={() => setShowNotificationPrefsModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
                                    </div>
                                    
                                    <div style={{ marginBottom: 16 }}>
                                        <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
                                            Choose which types of notifications you want to receive:
                                        </p>
                                        
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', padding: 12, background: '#f5f5f5', borderRadius: 6, cursor: 'pointer' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={notificationPrefs.pinRequests}
                                                    onChange={(e) => setNotificationPrefs({...notificationPrefs, pinRequests: e.target.checked})}
                                                    style={{ marginRight: 12, width: 18, height: 18, cursor: 'pointer' }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>PIN Code Requests</div>
                                                    <div style={{ fontSize: 13, color: '#666' }}>When users request new PIN codes</div>
                                                </div>
                                            </label>
                                            
                                            <label style={{ display: 'flex', alignItems: 'center', padding: 12, background: '#f5f5f5', borderRadius: 6, cursor: 'pointer' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={notificationPrefs.errors}
                                                    onChange={(e) => setNotificationPrefs({...notificationPrefs, errors: e.target.checked})}
                                                    style={{ marginRight: 12, width: 18, height: 18, cursor: 'pointer' }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>Error Reports</div>
                                                    <div style={{ fontSize: 13, color: '#666' }}>When frontend errors occur</div>
                                                </div>
                                            </label>
                                            
                                            <label style={{ display: 'flex', alignItems: 'center', padding: 12, background: '#f5f5f5', borderRadius: 6, cursor: 'pointer' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={notificationPrefs.pageVisits}
                                                    onChange={(e) => setNotificationPrefs({...notificationPrefs, pageVisits: e.target.checked})}
                                                    style={{ marginRight: 12, width: 18, height: 18, cursor: 'pointer' }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>Page Visits</div>
                                                    <div style={{ fontSize: 13, color: '#666' }}>When users visit pages (high volume)</div>
                                                </div>
                                            </label>
                                            
                                            <label style={{ display: 'flex', alignItems: 'center', padding: 12, background: '#f5f5f5', borderRadius: 6, cursor: 'pointer' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={notificationPrefs.pdfClicks}
                                                    onChange={(e) => setNotificationPrefs({...notificationPrefs, pdfClicks: e.target.checked})}
                                                    style={{ marginRight: 12, width: 18, height: 18, cursor: 'pointer' }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>PDF Button Clicks</div>
                                                    <div style={{ fontSize: 13, color: '#666' }}>When users click PDF export buttons</div>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                                        <button 
                                            onClick={() => setShowNotificationPrefsModal(false)}
                                            style={{ padding: '8px 16px', background: '#ccc', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={handleUpdateNotificationPrefs}
                                            disabled={prefsLoading}
                                            style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                        >
                                            {prefsLoading ? 'Saving...' : 'Save Preferences'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CSV Import modal */}
                        {canAddTests && showCSVModal && (
                            <div className="Modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }} onClick={() => setShowCSVModal(false)}>
                                <div className="csv-import-modal" style={{ width: '95%', maxWidth: 900, marginTop: 12, padding: 20, borderRadius: 8, background: '#fff', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <h4 style={{ margin: 0 }}>Import Users from CSV</h4>
                                        <button onClick={() => setShowCSVModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
                                    </div>
                                    
                                    <div style={{ marginBottom: 16, padding: 12, background: '#f0f0f0', borderRadius: 4 }}>
                                        <strong>CSV Format (supports both comma and pipe delimiters):</strong> 
                                        <div style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 13 }}>
                                            admin,email,bcc,company,language<br/>
                                            <em>or</em><br/>
                                            admin|email|bcc|company|language
                                        </div>
                                        <ul style={{ marginTop: 8, fontSize: 13 }}>
                                            <li><strong>admin</strong>: Email of an existing admin linked to the company</li>
                                            <li><strong>email</strong>: Email of the new user to create</li>
                                            <li><strong>bcc</strong>: (Optional) Email of an existing admin for notifications (can be blank)</li>
                                            <li><strong>company</strong>: Company name (must exist)</li>
                                            <li><strong>language</strong>: Language code (e.g., en, fr, es, de)</li>
                                        </ul>
                                        <div style={{ marginTop: 8, fontSize: 13, fontStyle: 'italic' }}>
                                            Example (comma-separated):<br/>
                                            <code>admin@example.com,newuser@example.com,bcc@example.com,MyCompany,en</code><br/>
                                            Example (pipe-separated):<br/>
                                            <code>admin@example.com|newuser@example.com|bcc@example.com|MyCompany|en</code>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 16 }}>
                                        <input 
                                            type="file" 
                                            accept=".csv,.txt"
                                            onChange={handleCSVFileChange}
                                            style={{ marginBottom: 8 }}
                                        />
                                        <button 
                                            onClick={handleCSVImport}
                                            disabled={!csvFile || csvImporting}
                                            style={{ marginLeft: 8 }}
                                        >
                                            {csvImporting ? 'Importing...' : 'Import'}
                                        </button>
                                    </div>

                                    {csvImportResults && (
                                        <div style={{ marginTop: 16 }}>
                                            <h5>Import Results:</h5>
                                            
                                            {csvImportResults.success.length > 0 && (
                                                <div style={{ marginBottom: 16 }}>
                                                    <strong style={{ color: 'green' }}>✓ Successfully created {csvImportResults.success.length} user(s):</strong>
                                                    <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ background: '#e8f5e9' }}>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Row</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Email</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Code</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Key</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Admin</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Company</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {csvImportResults.success.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc' }}>{item.row}</td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc' }}>{item.email}</td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc' }}>{item.code}</td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc', fontFamily: 'monospace' }}>{item.key}</td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc' }}>{item.admin}</td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc' }}>{item.company}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {csvImportResults.errors.length > 0 && (
                                                <div>
                                                    <strong style={{ color: 'red' }}>✗ Failed to create {csvImportResults.errors.length} user(s):</strong>
                                                    <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ background: '#ffebee' }}>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Row</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Data</th>
                                                                <th style={{ padding: 4, border: '1px solid #ccc' }}>Error</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {csvImportResults.errors.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc' }}>{item.row}</td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc', fontSize: 11, fontFamily: 'monospace' }}>
                                                                        {JSON.stringify(item.data)}
                                                                    </td>
                                                                    <td style={{ padding: 4, border: '1px solid #ccc', color: 'red' }}>{item.error}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            }
            
            <div className="Admin">
                {loading ?
                    <div style={{ width: "100vw", height: "100vh", zIndex: '9999', color: "black", position: 'fixed', top: "0", left: "0", fontSize: "20px", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                        <h3>Chargement...</h3>
                    </div>
                    :
                    <div className="Admin-content">
                        <div className="Admin-filters">
                                <div className="filters-title">Filters</div>
                                <div className="filters-desc">Use these controls to limit the user list. Select a company and/or admin, then clear to reset.</div>
                                <div className="filters-row">
                                    <div className="filter-item">
                                        <label>Company:</label>
                                    <Select
                                        isMulti
                                        options={companies.map(c => ({ value: c._id, label: c.name }))}
                                        value={companies.filter(c => companyFilter.includes(c._id)).map(c => ({ value: c._id, label: c.name }))}
                                        onChange={(selected) => {
                                            const vals = Array.isArray(selected) ? selected.map(s => s.value) : [];
                                            setCompanyFilter(vals);
                                            setCurrentPage(1);
                                        }}
                                            placeholder={companyFilter.length === 0 ? 'All companies' : `${companyFilter.length} selected`}
                                    />
                                </div>
                                    <div className="filter-item">
                                        <label>Admin:</label>
                                    <Select
                                        isMulti
                                        options={users.filter(u => String(u.authorities) === 'admin').map(a => ({ value: a._id, label: a.email || a.code }))}
                                        value={users.filter(u => adminFilter.includes(u._id)).map(a => ({ value: a._id, label: a.email || a.code }))}
                                        onChange={(selected) => {
                                            const vals = Array.isArray(selected) ? selected.map(s => s.value) : [];
                                            setAdminFilter(vals);
                                            setCurrentPage(1);
                                        }}
                                            placeholder={adminFilter.length === 0 ? 'All admins' : `${adminFilter.length} selected`}
                                    />
                                </div>
                                    <div className="filter-item filter-role">
                                        <label>Role:</label>
                                        <select value={roleFilter} className="filter-role-select" onChange={e => { setRoleFilter(e.target.value); setCurrentPage(1); }}>
                                        <option className="filter-role-option" value="all">All</option>
                                        <option className="filter-role-option" value="only-admins">Only: admins</option>
                                        <option className="filter-role-option" value="only-users">Only: users</option>
                                        <option className="filter-role-option" value="only-promo">Only: promo</option>
                                    </select>
                                    </div>
                                    <div className="filter-actions">
                                        <button
                                            className="filter-clear-btn"
                                            onClick={() => {
                                                // Reset filters and selected translator, clear search and reset pagination
                                                setCompanyFilter([]);
                                                setAdminFilter([]);
                                                setRoleFilter('all');
                                                setSelectedUser(null);
                                                setCurrentPage(1);
                                                setSearchTerm('');
                                                const searchInput = document.getElementById('Admin-searchBar');
                                                if (searchInput) searchInput.value = '';
                                                // Clear persisted filters
                                                try {
                                                    sessionStorage.removeItem('admin_companyFilter');
                                                    sessionStorage.removeItem('admin_adminFilter');
                                                    sessionStorage.removeItem('admin_roleFilter');
                                                } catch (e) {
                                                    // eslint-disable-next-line no-console
                                                    console.warn('Failed to clear persisted admin filters', e);
                                                }
                                            }}
                                        >
                                            Clear
                                        </button>
                                    </div>
                            </div>
                        </div>
                            <div className="Admin-stats">
                                <div className="stats-text">
                                    <strong>Stats:</strong> Total {stats.total} — Users {stats.users} — Admins {stats.admins} — Promo {stats.promo} — Completed {stats.completed} — Not completed {stats.notCompleted}
                                </div>
                                <div className="rows-per-page">
                                    <label>Rows per page:</label>
                                    <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value) || 0); setCurrentPage(1); }}>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={0}>All</option>
                                    </select>
                                    <button
                                        className="created-toggle-btn"
                                        onClick={() => setShowCreatedColumn(prev => !prev)}
                                        title="Show / hide Created column"
                                    >
                                        {showCreatedColumn ? 'Hide Created' : 'Show Created'}
                                    </button>
                                    <button
                                        className="created-toggle-btn"
                                        onClick={() => {
                                            // 3-step toggle: off -> desc -> asc -> off
                                            setDatePassedSort(prev => prev === null ? 'desc' : prev === 'desc' ? 'asc' : null);
                                            setCreatedSort(null);
                                            setCurrentPage(1);
                                        }}
                                        title="Sort by Date Passed"
                                        style={{ marginLeft: 8, background: datePassedSort ? '#007bff' : undefined, color: datePassedSort ? '#fff' : undefined }}
                                    >
                                        {datePassedSort === 'asc' ? 'Date Passed ▲' : datePassedSort === 'desc' ? 'Date Passed ▼' : 'Date Passed'}
                                    </button>
                                    {canAddTests && (
                                        <button
                                            className="created-toggle-btn"
                                            onClick={() => setShowCSVModal(true)}
                                            title="Import users from CSV"
                                            style={{ marginLeft: 8 }}
                                        >
                                            Import CSV
                                        </button>
                                    )}
                                </div>
                            </div>
+
+
+                        <table id="Admin-table" style={{ marginTop: 15 }}>
                            <thead>
                                <tr>
                                    <th className="Admin-id-th" scope="col" style={{ fontSize: '15px' }}>#</th>
                                    <th scope="col">Email</th>
                                    <th scope="col" className="admin-col">Option</th>
                                    {(isSuperAdmin || companies.length > 1) && <th scope="col">Company</th>}
                                    {/* Created column removed - created date moves to date-row when toggled */}
                                    {canAddTests && <th scope="col"><button className="Admin-action-new-btn" onClick={() => addUser()}>New</button></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedUsers.map((user, index) => {
                                    if (!user) return null;
                                    // Email validation regex
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    const isEmailValid = !!user?.email && emailRegex.test(user?.email);

                                    const dateValid = user && user.testPassed === true && user.dateSurveyPassed && Number.isFinite(new Date(user.dateSurveyPassed).getTime());
                                    const dateText = dateValid ? new Date(user.dateSurveyPassed).toLocaleDateString() : null;
                                    const colSpan = 3 + ((isSuperAdmin || companies.length > 1) ? 1 : 0) + (canAddTests ? 1 : 0);

                                    return (
                                        <Fragment key={(user && (user._id || user.code)) || index}>
                                            {dateText && (datePassedSort || showCreatedColumn) && (
                                                <tr className="datepassed-row">
                                                    <td colSpan={colSpan} style={{ padding: '6px 8px', background: '#fafafa', fontSize: 13 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <div>
                                                                <strong>Date Passed:</strong> {dateText}
                                                                <span style={{ marginLeft: 10 }}><strong>Key:</strong> <span style={{ fontFamily: 'monospace' }}>{user.key || '-'}</span></span>
                                                            </div>
                                                            <div style={{ color: '#666', fontSize: 12 }}>
                                                                <strong>Created:</strong> {user?.dateUserAdded ? new Date(user.dateUserAdded).toLocaleDateString() : ''}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            <tr key={index} className={user.testPassed ? 'passed':'not-passed'}>
                                                <td className="Admin-id-td">
                                                    {user.authorities === 'user'
                                                        ? user.showUser ? null : <span className='Review-User'>R</span>
                                                        : user.authorities === 'promo' ? <span className='Promo-Test'>P</span> : user.code === '1000'
                                                            ? <span className='Admin-isSuperAdmin'>SA</span>
                                                            : <span className="Admin-isAdmin">A</span>
                                                    }
                                                </td>
                                                <td className="email-td">
                                                    <div className="table-email">{user.email} </div>
                                                </td>
                                                <td className="options-td">
                                                    {user.testPassed ? (
                                                        <>
                                                        <Link 
                                                            to={{ pathname: '/userInfo', state: user }} 
                                                            className="pdf-btn"
                                                            onClick={() => frontendLogger.pdfClick('user_report', { userEmail: user.email })}
                                                        >
                                                            PDF
                                                        </Link>
                                                        
                                                        {(() => {
                                                            // Check if user is associated with "Patek Philippe" company
                                                            const userCompanyIds = Array.isArray(user.company) ? user.company : [user.company];
                                                            const userCompanies = companies.filter(c => userCompanyIds.includes(c._id));
                                                            const isPatekPhilippe = userCompanies.some(c => c.name === 'Patek Philippe');
                                                            return isPatekPhilippe || !isPatekPhilippe ? (
                                                                <div>
                                                                {(isSuperAdmin || code === '1098') && <Link 
                                                                    to={{ pathname: '/userInfoAndAttitudes', state: user }} 
                                                                    className="pdf-btn attitude-btn"
                                                                    onClick={() => frontendLogger.pdfClick('user_info_attitudes', { userEmail: user.email })}
                                                                >
                                                                    A
                                                                </Link>}
                                                                {isSuperAdmin && <Link
                                                                    to={{ pathname: '/attitudes', state: { user } }}
                                                                    className="pdf-btn attitudes-btn attitude-btn"
                                                                    title="View Attitudes Report"
                                                                    onClick={() => frontendLogger.pdfClick('attitudes_report', { userEmail: user.email })}
                                                                >
                                                                    A
                                                                </Link>}
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                        </>
                                                    ) : user.emailAlreadySent ? (
                                                        <button
                                                            id={user.code}
                                                            className="Admin-resend-btn"
                                                            onClick={() => sendMailUser(user.code, user.email, user.key)}
                                                            disabled={!isEmailValid}
                                                        >
                                                            Resend
                                                        </button>
                                                    ) : (
                                                        <button
                                                            id={user.code}
                                                            className="Admin-action-btn"
                                                            onClick={() => sendMailUser(user.code, user.email, user.key)}
                                                            disabled={!isEmailValid}
                                                        >
                                                            Send
                                                        </button>
                                                    )}
                                                </td>
                                                    {(isSuperAdmin || companies.length > 1) && (
                                                    <td>
                                                        {Array.isArray(user.company)
                                                            ? user.company.map(cid => {
                                                                // Try to get the name, fallback to ID if not found
                                                                const name = companies.find(c => c._id === cid)?.name;
                                                                return name || cid;
                                                            }).join(', ')
                                                            : (() => {
                                                                const name = companies.find(c => c._id === user.company)?.name;
                                                                return name || user.company || '';
                                                            })()
                                                        }
                                                    </td>
                                                )}

                                                {/* per-row Created column removed; created date shows in date-row when toggled */}

                                                <td className="edit-td">
                                                    <div className="Admin-select-btn" onClick={() => editOptions(user)}><div className={selectedUsers.includes(user._id) ? 'selected-user' : ''}></div></div>
                                                    <button 
                                                        className="Admin-analytics-btn" 
                                                        onClick={() => fetchUserAnalytics(user.email)}
                                                        title="View email analytics"
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '18px',
                                                            padding: '5px',
                                                            marginRight: '5px'
                                                        }}
                                                    >
                                                        📊
                                                    </button>
                                                   
                                                    <div className="Admin-edit-btn" onClick={() => editUser(user)}></div>
                                                </td>
                                            </tr>
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                            <div>Page {currentPage} / {totalPages}</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
                                <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
                            </div>
                        </div>
                    </div>
                }
                
                {editModal && 
                    <div>
                        <div className='Admin-modal-background'></div>
                        <div className='Admin-modal'>
                            <div className='modal-content' ref={editModalRef} >
                                <div className='modal-header'>
                                    <h2>Edit User</h2>
                                    <span className="modal-close-btn" onClick={() => closeEditUser()}>x</span>
                                </div>
                                <form className='Admin-form' onSubmit={editMode ? handleSubmitEdit : handleSubmit}>
                                    <label><p>Email:</p><input type='email' name='email' value={user?.email} onChange={updateUser} required /></label>
                                    <label>
                                        <p>BCC Email (optional):</p>
                                        <input 
                                            type='email' 
                                            name='bccEmail' 
                                            value={user?.bccEmail || ''} 
                                            onChange={updateUser}
                                            placeholder="Optional: BCC admin email for notifications"
                                        />
                                        <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                                            {user?.bccEmail 
                                                ? '✓ BCC will be added to all emails sent to this user' 
                                                : 'No BCC set - only admin@ppe-scope.ch will receive copies'}
                                        </small>
                                        {user?.bccEmail && (
                                            <small style={{ fontSize: '11px', color: '#999', display: 'block', marginTop: '2px', fontStyle: 'italic' }}>
                                                Note: The BCC email should be an admin linked to this user's company
                                            </small>
                                        )}
                                    </label>
                                    <div><span className="translate-label">Manager / Coach : {user.managerId} - - - {getManagerEmail(user.managerId)}</span></div>
                                        <div>
                                        { isSuperAdmin &&
                                            <div className="Admin-role-select">
                                                <label><p>Company:</p>
                                                    <select
                                                    name='company'
                                                    value={user?.company || []}
                                                    onChange={e => {
                                                        // For multi-select
                                                        const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                                        const selectedNames = (companies || [])
                                                            .filter(c => selected.includes(c._id))
                                                            .map(c => c.name);
                                                        // Update local user state for both id(s) and readable name(s)
                                                        setUser(prev => ({ ...(prev || {}), company: selected, companyName: selectedNames }));
                                                        // Persist the company id(s) change via updateUser (single call)
                                                        updateUser({ target: { name: 'company', value: selected } });
                                                    }}
                                                    multiple
                                                    required
                                                >
                                                    {companies?.map(company => (
                                                        <option key={company._id} value={company._id}>{company.name}</option>
                                                    ))}
                                                </select>
                                                </label>
                                            </div>
                                             }
                                            <div className="Admin-role-select">
                                                <label><p>Language:</p>
                                                    <select name='language' value={user?.language} onChange={updateUser} required>
                                                        {languages?.map(language => {
                                                            return (
                                                                <option className="pointer" key={language._id} value={language._id}>{language.name}</option>
                                                            )
                                                        })}
                                                    </select>
                                                </label>
                                            </div>
                                                <div className="Admin-role-select">
                                                COMPANY NAME:<br />{
                                                    (() => {
                                                        const companyIds = user?.company;
                                                        if (Array.isArray(companyIds) && companyIds.length > 0) {
                                                            const names = (companies || []).filter(c => companyIds.includes(c._id)).map(c => c.name);
                                                            return (
                                                                <>
                                                                    {names.length ? names.join(', ') : 'N/A'}
                                                                    {names.length > 0 && (
                                                                        <input
                                                                            type="hidden"
                                                                            name="company-name"
                                                                            value={names.join(', ')}
                                                                            readOnly
                                                                            style={{ marginLeft: 8 }}
                                                                        />
                                                                    )}
                                                                </>
                                                            );
                                                        }

                                                        const single = (companies || []).find(c => c._id === user?.company);
                                                        if (single) return single.name;
                                                        return user?.company || 'N/A';
                                                    })()
                                                }
                                                
                                                <label><p>Role Label:</p>
                                                    <select 
                                                        name='roleLabel' 
                                                        value={user?.roleLabel || ''} 
                                                        onChange={updateUser}
                                                    >
                                                        <option value="">-- Select Role --</option>
                                                        {roleLabels[user?.language || 'en']?.map(role => (
                                                            <option className="pointer" key={role.value} value={role.value}>
                                                                {role.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                                                    {user?.roleLabel 
                                                        ? `Current: ${roleLabels[user?.language || 'en']?.find(r => r.value === user.roleLabel)?.label || user.roleLabel}` 
                                                        : 'No role label set'}
                                                </small>
                                            </div>
                                            <div className="Admin-role-select">
                                                <label><p>Email Template:</p>
                                                    <select 
                                                        name='emailTemplate' 
                                                        value={user?.emailTemplate || ''} 
                                                        onChange={updateUser}
                                                    >
                                                        <option value="">-- Standard (Default) --</option>
                                                        <option value="standard">Standard</option>
                                                        <option value="deadline">With Deadline</option>
                                                    </select>
                                                </label>
                                                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                                                    {user?.emailTemplate === 'deadline' 
                                                        ? 'Current: With Deadline (includes Nov 14 deadline)' 
                                                        : 'Current: Standard (no deadline)'}
                                                </small>
                                            </div>
                                        </div>
                                   
                                    {(user.adminAuthorities?.edit?.enabled || user.adminAuthorities?.translator?.enabled) &&
                                    <div>
                                        <div>
                                        <span className="translate-label">Edit : </span>
                                        {Array.isArray(user.adminAuthorities?.edit?.languages) && user.adminAuthorities?.edit.languages.length > 0 && (
                                            user.adminAuthorities?.edit.languages.map(lang =>
                                                user.code === code || isSuperAdmin ? (
                                                    <Link
                                                        key={lang}
                                                        to={{
                                                            pathname: '/userInfo/edit',
                                                            state: { ...user, editLanguage: lang }
                                                        }}
                                                        className="edit-text-btn"
                                                        style={{
                                                            marginLeft: 8,
                                                            backgroundImage: `url(${flagMap[lang]})`,
                                                            backgroundRepeat: 'no-repeat',
                                                            backgroundPosition: lang === 'zh-TW' ? 'left center' : 'center center',
                                                            backgroundSize: (lang === 'zh' || lang === 'zh-TW') ? 'contain' : 'cover',
                                                            backgroundColor: lang === 'zh' ? '#de2910' : (lang === 'zh-TW' ? '#fe0000' : 'transparent')
                                                        }}
                                                        
                                                    >
                                                        {/*lang.toUpperCase()*/}
                                                    </Link>
                                                ) : (
                                                    <span
                                                        key={lang}
                                                        className="edit-text-btn edit-text-btn-disabled"
                                                        title="You do not have permission to edit"
                                                    >
                                                        ({lang.toUpperCase()})
                                                    </span>
                                                )
                                            )
                                        )}
                                        </div>
                                        { /*console.log("user.code", user.code,code) */ }
                                        <div>
                                            <span className="translate-label">Translate : </span>
                                        <div className="translate-pair-box">
                                            {getUserTranslationPairs(user.code).map((pair, idx) => (
                                            <div className="translate-pair" key={pair.from + '-' + pair.to}>
                                                {user.code === code || isSuperAdmin ? (
                                                <>
                                                    <Link
                                                    key={'from-' + pair.from}
                                                    to={{
                                                        pathname: '/userInfo/translate',
                                                        state: { ...user, from: pair.from, to: pair.to }
                                                    }}
                                                    className="edit-text-btn"
                                                    style={{
                                                        marginLeft: 8,
                                                        backgroundImage: `url(${flagMap[pair.from]})`,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: pair.from === 'zh-TW' ? 'left center' : 'center center',
                                                        backgroundSize: (pair.from === 'zh' || pair.from === 'zh-TW') ? 'contain' : 'cover',
                                                        backgroundColor: pair.from === 'zh' ? '#de2910' : (pair.from === 'zh-TW' ? '#fe0000' : 'transparent')
                                                    }}
                                                    >
                                                    {/*pair.from.toUpperCase()*/}
                                                    </Link>
                                                    <div className="translate-arrow">→ </div>
                                                    <Link
                                                    key={'to-' + pair.to}
                                                    to={{
                                                        pathname: '/userInfo/translate',
                                                        state: { ...user, from: pair.from, to: pair.to }
                                                    }}
                                                    className="edit-text-btn"
                                                    style={{
                                                        marginLeft: 8,
                                                        backgroundImage: `url(${flagMap[pair.to]})`,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: pair.to === 'zh-TW' ? 'left center' : 'center center',
                                                        backgroundSize: (pair.to === 'zh' || pair.to === 'zh-TW') ? 'contain' : 'cover',
                                                        backgroundColor: pair.to === 'zh' ? '#de2910' : (pair.to === 'zh-TW' ? '#fe0000' : 'transparent')
                                                    }}
                                                    >
                                                    {/*pair.to.toUpperCase()*/}
                                                    </Link>
                                                </>
                                                ) : (
                                                <>
                                                    <span
                                                    key={'from-' + pair.from}
                                                    className="edit-text-btn edit-text-btn-disabled"
                                                    title="You do not have permission to translate"
                                                    style={{
                                                        marginLeft: 8,
                                                        backgroundImage: ``,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: 'center',
                                                        backgroundSize: 'cover'
                                                    }}
                                                    >
                                                    ({pair.from.toUpperCase()})
                                                    </span>
                                                    <div className="translate-arrow">→ </div>
                                                    <span
                                                    key={'to-' + pair.to}
                                                    className="edit-text-btn edit-text-btn-disabled"
                                                    title="You do not have permission to translate"
                                                    style={{
                                                        marginLeft: 8,
                                                        backgroundImage: ``,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: 'center',
                                                        backgroundSize: 'cover'
                                                    }}
                                                    >
                                                    ({pair.to.toUpperCase()})
                                                    </span>
                                                </>
                                                )}
                                            </div>
                                            ))}
                                        </div>
                                        </div>
                                    </div>
                                    }
                                    <p><b>Key:</b>  {user?.key}</p>
                                    {isSuperAdmin &&
                                        <div>
                                            <label>
                                            <p>Change Role:</p>
                                            <select
                                                name="authorities"
                                                value={user?.authorities && user.authorities !== false ? user.authorities : 'user'}
                                                onChange={e => {
                                                    const value = e.target.value;
                                                    const newUser = { ...user };
                                                    newUser.authorities = value;
                                                    newUser.adminAuthorities = value === 'admin'
                                                        ? {
                                                            contentEditor: false,
                                                            translator: { enabled: false, pairs: [] },
                                                            edit: { enabled: false, languages: [] },
                                                            uniqueEdit: { enabled: false, languages: [] }
                                                        }
                                                        : false;
                                                    setUser(newUser);
                                                    updateUser({ target: { name: 'authorities', value } });
                                                }}
                                                required
                                            >
                                                <option className="pointer" value="user">User</option>
                                                <option className="pointer" value="admin">Admin</option>
                                                <option className="pointer" value="promo">Promo</option>
                                            </select>
                                            </label>

                                            {/* Show admin options if authorities is an object */}
                                            {user?.authorities && user.authorities !== false && (
                                            <div className="admin-authorities-card">
                                                <h4 onClick={() => toggleAdminPermissions()} className="admin-authorities-title">Admin Permissions</h4>
                                                { openAdminPermissions && <div>
                                                <div className="admin-authority-row">
                                                <label className="admin-checkbox">
                                                    <div className="admin-inline-checkbox">
                                                    <span>Content Editor</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!user.adminAuthorities?.contentEditor}
                                                        onChange={e => {
                                                            const checked = e.target.checked;
                                                            const updatedAuthorities = {
                                                                ...user.adminAuthorities,
                                                                contentEditor: checked,
                                                                ...(checked
                                                                    ? {}
                                                                    : {
                                                                        translator: { enabled: false, pairs: [] },
                                                                        edit: { enabled: false, languages: [] },
                                                                        uniqueEdit: { enabled: false, languages: [] }
                                                                    }
                                                                )
                                                            };
                                                            updateUser({
                                                                target: {
                                                                    name: 'adminAuthorities',
                                                                    value: updatedAuthorities,
                                                                    authorities: user.authorities // <-- always send this
                                                                }
                                                            });
                                                        }}
                                                    />
                                                    </div>
                                                </label>
                                                </div>

                                                {user.adminAuthorities?.contentEditor && (
                                                <>
                                                    {/* Translator */}
                                                    <div className="admin-authority-row">
                                                    <label className="admin-checkbox">
                                                        <div className="admin-inline-checkbox">
                                                        <span className="content-editor-span">Translator</span>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!user.adminAuthorities?.translator?.enabled}
                                                            onChange={e => {
                                                            const prevTranslator = user.adminAuthorities?.translator || { enabled: false, pairs: [] };
                                                            const updatedAuthorities = {
                                                                ...user.adminAuthorities,
                                                                translator: {
                                                                ...prevTranslator,
                                                                enabled: e.target.checked,
                                                                pairs: e.target.checked ? prevTranslator.pairs : []
                                                                }
                                                            };
                                                            updateUser({
                                                                target: {
                                                                    name: 'adminAuthorities',
                                                                    value: updatedAuthorities,
                                                                    authorities: user.authorities // <-- always send this
                                                                }
                                                            });
                                                            }}
                                                        />
                                                        </div>
                                                    </label>
                                                    {user.adminAuthorities?.translator?.enabled && (
                                                        <div className="admin-authority-input">
                                                        <label>
                                                            <span>Translation Pairs</span>
                                                            <div className="admin-authority-checkbox-list">
                                                            {languages
                                                                .filter(lang => lang._id !== "fr")
                                                                .map(lang => {
                                                                const checked = (user.adminAuthorities?.translator.pairs || []).some(
                                                                    pair => pair.from === "fr" && pair.to === lang._id
                                                                );
                                                                return (
                                                                    <label
                                                                    key={lang._id}
                                                                    className={`admin-authority-checkbox-item${checked ? " checked" : ""}`}
                                                                    >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        onChange={e => {
                                                                        const prevTranslator = user.adminAuthorities?.translator || { enabled: false, pairs: [] };
                                                                        let pairs = prevTranslator.pairs || [];
                                                                        if (e.target.checked) {
                                                                            pairs = [...pairs, { from: "fr", to: lang._id }];
                                                                        } else {
                                                                            pairs = pairs.filter(
                                                                            pair =>
                                                                                !(
                                                                                pair.from === "fr" &&
                                                                                pair.to === lang._id
                                                                                )
                                                                            );
                                                                        }
                                                                        const updatedAuthorities = {
                                                                            ...user.adminAuthorities,
                                                                            translator: {
                                                                            ...prevTranslator,
                                                                            enabled: pairs.length > 0,
                                                                            pairs
                                                                            }
                                                                        };
                                                                        updateUser({
                                                                target: {
                                                                    name: 'adminAuthorities',
                                                                    value: updatedAuthorities,
                                                                    authorities: user.authorities // <-- always send this
                                                                }
                                                            });
                                                                        }}
                                                                    />
                                                                    French (fr) to {lang.name}
                                                                    </label>
                                                                );
                                                                })}
                                                            </div>
                                                        </label>
                                                        </div>
                                                    )}
                                                    </div>
                                                    {/* Edit */}
                                                    <div className="admin-authority-row">
                                                    <label className="admin-checkbox">
                                                        <div className="admin-inline-checkbox">
                                                        <span className="content-editor-span">Edit</span>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!user.adminAuthorities?.edit?.enabled}
                                                            onChange={e => {
                                                            const updatedAuthorities = {
                                                                ...user.adminAuthorities,
                                                                edit: {
                                                                ...(user.adminAuthorities?.edit || { languages: [] }),
                                                                enabled: e.target.checked
                                                                }
                                                            };
                                                            updateUser({
                                                                target: {
                                                                    name: 'adminAuthorities',
                                                                    value: updatedAuthorities,
                                                                    authorities: user.authorities // <-- always send this
                                                                }
                                                            });
                                                            }}
                                                        />
                                                        </div>
                                                    </label>
                                                    {user.adminAuthorities?.edit?.enabled && (
                                                        <div className="admin-authority-input">
                                                        <label>
                                                            <span>Edit Languages</span>
                                                            <div className="admin-authority-checkbox-list">
                                                            {languages.map(lang => {
                                                                const checked = (user.adminAuthorities?.edit.languages || []).includes(lang._id);
                                                                return (
                                                                <label
                                                                    key={lang._id}
                                                                    className={`admin-authority-checkbox-item${checked ? " checked" : ""}`}
                                                                >
                                                                    <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={e => {
                                                                        let selected = user.adminAuthorities?.edit.languages || [];
                                                                        if (e.target.checked) {
                                                                        selected = [...selected, lang._id];
                                                                        } else {
                                                                        selected = selected.filter(id => id !== lang._id);
                                                                        }
                                                                        const updatedAuthorities = {
                                                                        ...user.adminAuthorities,
                                                                        edit: {
                                                                            ...(user.adminAuthorities?.edit || {}),
                                                                            languages: selected,
                                                                            enabled: selected.length > 0
                                                                        }
                                                                        };
                                                                        updateUser({
                                                                            target: {
                                                                                name: 'adminAuthorities',
                                                                                value: updatedAuthorities,
                                                                                authorities: user.authorities // <-- always send this
                                                                            }
                                                                        });
                                                                    }}
                                                                    />
                                                                    {lang.name}
                                                                </label>
                                                                );
                                                            })}
                                                            </div>
                                                        </label>
                                                        </div>
                                                    )}
                                                    </div>
                                                    {/* Unique Edit */}
                                                    <div className="admin-authority-row">
                                                    <label className="admin-checkbox">
                                                        <div className="admin-inline-checkbox">
                                                        <span className="content-editor-span">Unique Edit</span>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!user.adminAuthorities?.uniqueEdit?.enabled}
                                                            onChange={e => {
                                                            const updatedAuthorities = {
                                                                ...user.adminAuthorities,
                                                                uniqueEdit: {
                                                                ...(user.adminAuthorities?.uniqueEdit || { languages: [] }),
                                                                enabled: e.target.checked
                                                                }
                                                            };
                                                            updateUser({
                                                                target: {
                                                                    name: 'adminAuthorities',
                                                                    value: updatedAuthorities,
                                                                    authorities: user.authorities // <-- always send this
                                                                }
                                                            });
                                                            }}
                                                        />
                                                        </div>
                                                    </label>
                                                    {user.adminAuthorities?.uniqueEdit?.enabled && (
                                                        <div className="admin-authority-input">
                                                        <label>
                                                            <span>Unique Edit Languages</span>
                                                            <div className="admin-authority-checkbox-list">
                                                            {languages.map(lang => {
                                                                const checked = (user.adminAuthorities?.uniqueEdit.languages || []).includes(lang._id);
                                                                return (
                                                                <label
                                                                    key={lang._id}
                                                                    className={`admin-authority-checkbox-item${checked ? " checked" : ""}`}
                                                                >
                                                                    <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={e => {
                                                                        let selected = user.adminAuthorities?.uniqueEdit.languages || [];
                                                                        if (e.target.checked) {
                                                                        selected = [...selected, lang._id];
                                                                        } else {
                                                                        selected = selected.filter(id => id !== lang._id);
                                                                        }
                                                                        const updatedAuthorities = {
                                                                        ...user.adminAuthorities,
                                                                        uniqueEdit: {
                                                                            ...(user.adminAuthorities?.uniqueEdit || {}),
                                                                            languages: selected,
                                                                            enabled: selected.length > 0
                                                                        }
                                                                        };
                                                                        updateUser({
                                                                            target: {
                                                                                name: 'adminAuthorities',
                                                                                value: updatedAuthorities,
                                                                                authorities: user.authorities // <-- always send this
                                                                            }
                                                                        });
                                                                    }}
                                                                    />
                                                                    {lang.name}
                                                                </label>
                                                                );
                                                            })}
                                                            </div>
                                                        </label>
                                                        </div>
                                                    )}
                                                    </div>
                                                </>
                                                )}
                                                </div>
                                            }
                                            </div>
                                            )}
                                            <div className='Admin-submit'><button type='button' onClick={() => sendData2User(user?._id)}>Archive</button></div>
                                            <div className='Admin-submit'><button type='button' onClick={() => archiveUser(user?._id)}>Send</button></div>
                                        </div>
                                    }
                                    
                                    <div className='Admin-submit'><button type='button' onClick={() => deleteUser(user?._id)}>Delete user</button></div>

                                </form>
                            </div>
                        </div>
                    </div> 
                }

                {selectedModal && 
                    <div>
                        <div className="selected-modal-content">
                            {/* Close box X in top right corner */}
                            <span
                                className="selected-modal-close"
                                onClick={() => {
                                    setSelectedModal(false);
                                    setSelectedUsers([]);
                                }}
                                title="Close"
                            >
                                ×
                            </span>
                            Selected : {selectedUsers.length} user(s)
                            { selectedUsers.length>0 &&  <button
                                className="Admin-delete-selected-btn"
                                onClick={deleteSelectedUsers}
                                disabled={selectedUsers.length === 0}
                                >
                                Delete Selected Users
                            </button>}
                        </div>
                    </div> 
                }
                
                {showModal && 
                    <div className='Admin-modal'>
                        <div className='modal-content' ref={modalRef}>
                            <div className='modal-header'>
                                <h2>Add Licence(s)</h2>
                                <span className="modal-close-btn" onClick={() => closeModal()}>x</span>
                            </div>
                            <form className='Admin-form' onSubmit={editMode ? handleSubmitEdit : handleSubmit}>
                                <label><p>N° Licences:</p><input type='number' defaultValue={licence} name='number' min={1} onChange={e => setLicence(Number(e.target.value))} required /></label> 
                                <label><p>Email:</p>
                                    <input type='email' name='email' value={user?.email} onChange={editMode ? handleSubmitEdit : handleChange} /></label> 
                                <label><p>Language:</p>
                                    <select name='language' value={user?.language} onChange={handleChange} required>
                                        <option value='fr' onChange={handleChange}>fr</option>
                                        <option value='en' onChange={handleChange}>en</option>
                                        <option value='de' onChange={handleChange}>de</option>
                                    </select>
                                </label>

                                {isSuperAdmin && (
                                <>
                                    <label>
                                    <p>Company:</p>
                                    <select
                                        name='company'
                                        value={user?.company}
                                        onChange={e => {
                                        handleChange(e);
                                        // Set default admin when company changes
                                        const admins = handleCompanyAdmins(e.target.value);
                                        if (admins.length > 0 && admins[0]._id !== 'none') {
                                            setUser(prev => ({ ...prev, admin: admins[0]._id }));
                                        } else {
                                            setUser(prev => ({ ...prev, admin: '' }));
                                        }
                                        }}
                                        required
                                    >
                                        {companies?.map(company => (
                                        <option key={company._id} value={company._id}>
                                            {company.name}
                                        </option>
                                        ))}
                                    </select>
                                    </label>
                                    {/* Company Admins select */}
                                    <label>
                                    <p>Administrator:</p>
                                    <select
                                        name="admin"
                                        value={user?.admin || (() => {
                                        const admins = handleCompanyAdmins(user?.company);
                                        return admins.length > 0 && admins[0]._id !== 'none' ? admins[0]._id : '';
                                        })()}
                                        onChange={handleChange}
                                    >
                                        <option value="">-- Select administrator --</option>
                                        {handleCompanyAdmins(user?.company).map(adminUser => (
                                        <option key={adminUser._id} value={adminUser.code}>
                                            {adminUser.email}
                                        </option>
                                        ))}
                                    </select>
                                    </label>
                                </>
                                )}

                                <label><p>Authorities:</p>
                                    <select name='authorities' value={user?.authorities} onChange={handleChange} required>
                                        <option value='user' onChange={handleChange}>User</option>
                                        <option value='admin' onChange={handleChange}>Admin</option>
                                        <option value='promo' onChange={handleChange}>Promo</option>
                                    </select>
                                </label>

                                <div className='Admin-submit'><button type='submit'>{editMode ? 'Edit licences' : 'Add licences'}</button></div>
                            </form>
                        </div>
                    </div>
                }
                
                {logout &&
                    <div className='Admin-modal'>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h2>Log out</h2>
                                <span className="modal-close-btn" onClick={() => setLogout(false)}>x</span>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure to log out ?</p>
                            </div>
                            <div className='modal-footer'>
                                <button className='modal-footer-logout' onClick={() => signOut()}>Log out</button>
                                <button className='modal-footer-no' onClick={() => setLogout(false)}>No</button>
                            </div>
                        </div>
                    </div>
                }
                
                {showTranslationChanges && 
                    <div className='Admin-modal translation-changes-modal'>
                        <div className='modal-content translation-changes-content'>
                            <div className='modal-header'>
                                <h2>
                                    History of Edits
                                    {selectedUser && selectedUser !== 'all' && (
                                        <span style={{fontSize: '14px', fontWeight: 'normal'}}>
                                            {' '}- User {selectedUser}
                                        </span>
                                    )}
                                </h2>
                                <span className="modal-close-btn" onClick={() => setShowTranslationChanges(false)}>×</span>
                            </div>
                            <div className="translation-changes-body">
                                {translationLoading ? (
                                    <div className="loading-spinner">
                                        <h3>Loading translation changes...</h3>
                                    </div>
                                ) : translationChanges ? (
                                    <div className="translation-changes-content-area">
                                        <div className="translation-summary">
                                            <h3>Language: {translationChanges.language?.toUpperCase()}</h3>
                                            <p>Total Changes: {translationChanges.total_changes}</p>
                                        </div>
                                        
                                        <div className="translation-changes-list">
                                            {translationChanges.changes?.map((change, index) => (
                                                <div key={index} className="change-entry">
                                                    <div className="change-header">
                                                        <h4>
                                                            Change {index + 1} - {change.timestamp} 
                                                            {change.user && ` by User ${change.user}`}
                                                        </h4>
                                                        {change.note && <span className="change-note">{change.note}</span>}
                                                    </div>
                                                    
                                                    <div className="change-details">
                                                        {change.changes?.map((detail, detailIndex) => (
                                                            <div key={detailIndex} className={`change-item ${detail.type}`}>
                                                                <div className="change-type-badge">{detail.type}</div>
                                                                <div className="change-content">
                                                                    <strong>Key:</strong> <code>{detail.key}</code>
                                                                    {detail.old_value && (
                                                                        <div className="old-value">
                                                                            <strong>Old:</strong> {detail.old_value}
                                                                        </div>
                                                                    )}
                                                                    {detail.new_value && (
                                                                        <div className="new-value">
                                                                            <strong>New:</strong> {detail.new_value}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no-changes">
                                        <p>No translation changes found.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                }
                
                {showUserSelection && 
                    <div className='Admin-modal'>
                        <div className='modal-content' style={{maxWidth: '500px'}}>
                            <div className='modal-header'>
                                <h2>Select User & Language</h2>
                                <span className="modal-close-btn" onClick={() => setShowUserSelection(false)}>×</span>
                            </div>
                            <div className="Admin-form" style={{padding: '20px'}}>
                                <div style={{marginBottom: '20px'}}>
                                    <label>
                                        <p><strong>Select Translator:</strong></p>
                                        <select 
                                            value={selectedUser || ''} 
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                            style={{
                                                width: '95%',
                                                padding: '10px',
                                                borderRadius: '5px',
                                                border: '1px solid #ddd',
                                                fontSize: '16px'
                                            }}
                                        >
                                            <option value="">-- Select a translator --</option>
                                            <option value="all">All Translators</option>
                                            {translatorUsers.map(user => (
                                                <option key={user.code} value={user.code}>
                                                    {user.email} (Code: {user.code}) - Can edit: {user.editLanguages?.join(', ')}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                                
                                <div style={{marginBottom: '20px'}}>
                                    <label>
                                        <p><strong>Select Language:</strong></p>
                                        <select 
                                            value={selectedLang} 
                                            onChange={(e) => setSelectedLang(e.target.value)}
                                            style={{
                                                width: '95%',
                                                padding: '10px',
                                                borderRadius: '5px',
                                                border: '1px solid #ddd',
                                                fontSize: '16px'
                                            }}
                                        >
                                            {isSuperAdmin ? (
                                                selectedUser && selectedUser !== 'all' ? (
                                                    getUserToLanguages(selectedUser).map((lang, index) => (
                                                        <option key={index} value={lang}>
                                                            {lang.toUpperCase()} (target language)
                                                        </option>
                                                    ))
                                                ) : (
                                                    <>
                                                        <option value="fr">French (fr)</option>
                                                        <option value="en">English (en)</option>
                                                        <option value="de">German (de)</option>
                                                        <option value="ja">Japanese (ja)</option>
                                                    </>
                                                )
                                            ) : (
                                                getUserToLanguages(code).map((lang, index) => (
                                                    <option key={index} value={lang}>
                                                        {lang.toUpperCase()} (target language)
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </label>
                                </div>
                                
                                {!isSuperAdmin && isMultiLanguageTranslator(code) && (
                                    <div style={{marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                                        <p style={{margin: '0 0 5px 0', fontSize: '14px', color: '#666'}}>
                                            <strong>Your translation pairs:</strong>
                                        </p>
                                        {getUserLanguagePairs(code)?.map((pair, index) => (
                                            <div key={index} style={{fontSize: '12px', color: '#888'}}>
                                                • {pair.from.toUpperCase()} → {pair.to.toUpperCase()}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                                    <button 
                                        type="button"
                                        onClick={() => setShowUserSelection(false)}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '5px',
                                            border: '1px solid #ccc',
                                            backgroundColor: '#f5f5f5',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            if (selectedUser) {
                                                const userId = selectedUser === 'all' ? null : selectedUser;
                                                getTranslationChanges(selectedLang, userId);
                                            } else {
                                                alert('Please select a translator');
                                            }
                                        }}
                                        disabled={!selectedUser}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '5px',
                                            border: 'none',
                                            backgroundColor: selectedUser ? '#c395ff' : '#ccc',
                                            color: 'white',
                                            cursor: selectedUser ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        View Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                }

                {/* Email Analytics Modal */}
                {showAnalyticsModal && 
                    <div className='Admin-modal' onClick={() => setShowAnalyticsModal(false)}>
                        <div className='modal-content' style={{maxWidth: '900px', maxHeight: '80vh', overflow: 'auto'}} onClick={(e) => e.stopPropagation()}>
                            <div className='modal-header'>
                                <h2>📊 Email Analytics - {selectedAnalyticsUser}</h2>
                                <span className="modal-close-btn" onClick={() => setShowAnalyticsModal(false)}>×</span>
                            </div>
                            <div className="modal-body" style={{padding: '20px'}}>
                                {analyticsLoading ? (
                                    <div style={{textAlign: 'center', padding: '40px'}}>
                                        <h3>Loading analytics...</h3>
                                    </div>
                                ) : analyticsData && analyticsData.length > 0 ? (
                                    <div>
                                        <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px'}}>
                                            <h3 style={{marginTop: 0}}>Summary</h3>
                                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px'}}>
                                                <div>
                                                    <strong>Total Emails:</strong> {analyticsData.length}
                                                </div>
                                                <div>
                                                    <strong>Opened:</strong> {analyticsData.filter(a => a.opened).length} ({Math.round(analyticsData.filter(a => a.opened).length / analyticsData.length * 100)}%)
                                                </div>
                                                <div>
                                                    <strong>Clicked:</strong> {analyticsData.filter(a => a.clicked).length} ({Math.round(analyticsData.filter(a => a.clicked).length / analyticsData.length * 100)}%)
                                                </div>
                                            </div>
                                        </div>

                                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px'}}>
                                            <thead>
                                                <tr style={{backgroundColor: '#c395ff', color: 'white'}}>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Type</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Sent</th>
                                                    <th style={{padding: '10px', textAlign: 'center'}}>Opened</th>
                                                    <th style={{padding: '10px', textAlign: 'center'}}>Opens</th>
                                                    <th style={{padding: '10px', textAlign: 'center'}}>Clicked</th>
                                                    <th style={{padding: '10px', textAlign: 'center'}}>Clicks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analyticsData.map((record, index) => (
                                                    <tr key={index} style={{borderBottom: '1px solid #ddd'}}>
                                                        <td style={{padding: '10px'}}>
                                                            {record.emailType === 'invitation' ? '📧 Invitation' : 
                                                             record.emailType === 'pdf_report' ? '📄 PDF Report' :
                                                             record.emailType === 'test_completion' ? '🎯 Test Complete' : 
                                                             '📧 Email'}
                                                        </td>
                                                        <td style={{padding: '10px'}}>
                                                            {new Date(record.sentAt).toLocaleString()}
                                                        </td>
                                                        <td style={{padding: '10px', textAlign: 'center'}}>
                                                            {record.opened ? (
                                                                <span style={{color: 'green'}}>
                                                                    ✓ {record.openedAt ? new Date(record.openedAt).toLocaleString() : ''}
                                                                </span>
                                                            ) : (
                                                                <span style={{color: '#999'}}>-</span>
                                                            )}
                                                        </td>
                                                        <td style={{padding: '10px', textAlign: 'center'}}>
                                                            {record.openCount || 0}
                                                        </td>
                                                        <td style={{padding: '10px', textAlign: 'center'}}>
                                                            {record.clicked ? (
                                                                <span style={{color: 'green'}}>
                                                                    ✓ {record.clickedAt ? new Date(record.clickedAt).toLocaleString() : ''}
                                                                </span>
                                                            ) : (
                                                                <span style={{color: '#999'}}>-</span>
                                                            )}
                                                        </td>
                                                        <td style={{padding: '10px', textAlign: 'center'}}>
                                                            {record.clickCount || 0}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
                                        <p>No email analytics found for this user.</p>
                                        <p style={{fontSize: '0.9em', marginTop: '20px'}}>📧 Tracked email types: Test invitations, PDF reports, and test completion notifications</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                }

                {/* PIN Codes Modal (superadmin) */}
                {showPinModal &&
                    <div className='Admin-modal' onClick={() => setShowPinModal(false)}>
                        <div className='modal-content' style={{maxWidth: '1000px', maxHeight: '80vh', overflow: 'auto'}} onClick={(e) => e.stopPropagation()}>
                            <div className='modal-header'>
                                <h2>🔐 Auth PIN Codes</h2>
                                <span className="modal-close-btn" onClick={() => setShowPinModal(false)}>×</span>
                            </div>
                            <div className="modal-body" style={{padding: '20px'}}>
                                {pinLoading ? (
                                    <div style={{textAlign: 'center', padding: '40px'}}>
                                        <h3>Loading PIN codes...</h3>
                                    </div>
                                ) : pinCodes && pinCodes.length > 0 ? (
                                    <div>
                                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                                            <thead>
                                                <tr style={{backgroundColor: '#444', color: 'white'}}>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Created</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>User ID</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>User Code</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Key</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>PIN</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Status</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Expires</th>
                                                    <th style={{padding: '10px', textAlign: 'left'}}>Used At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pinCodes.map((p, idx) => (
                                                    <>
                                                    <tr className="pin-row" style={{borderBottom: '1px solid #eee'}}>
                                                        <td data-label="Timestamp" className="col-timestamp" style={{padding: '8px', display: 'flex', alignItems: 'center', gap: 8}}>
                                                            <button
                                                                className={`mobile-expand-btn ${expandedPinId === p.id ? 'expanded' : ''}`}
                                                                onClick={() => toggleExpandPin(p.id)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpandPin(p.id); } }}
                                                                aria-expanded={expandedPinId === p.id}
                                                                aria-controls={`pin-details-${p.id}`}
                                                                style={{display: 'inline-block'}}
                                                                title={expandedPinId === p.id ? 'Collapse details' : 'Expand details'}
                                                            >▾</button>
                                                            <span className="truncate" style={{minWidth: 0}}>{p.id ? new Date(parseInt(p.id.substring(0,8),16)*1000).toLocaleString() : ''}</span>
                                                        </td>
                                                        <td data-label="User" className="col-user" style={{padding: '8px'}}>
                                                            <span className="truncate" title={p.userEmail || p.userId || ''}>{p.userEmail || p.userId || ''}</span>
                                                            <button className="copy-btn" onClick={() => copyToClipboard(p.userEmail || p.userId)} aria-label="Copy user">⧉</button>
                                                        </td>
                                                        <td data-label="User Code" className="col-usercode hide-mobile" style={{padding: '8px'}}>{p.userCode || ''}</td>
                                                        <td data-label="Key" className="col-key hide-mobile" style={{padding: '8px'}}>
                                                            <span className="truncate" title={p.key || ''}>{p.key || ''}</span>
                                                            <button className="copy-btn" onClick={() => copyToClipboard(p.key)} aria-label="Copy key">⧉</button>
                                                        </td>
                                                        <td data-label="PIN" className="col-pin" style={{padding: '8px'}}>{p.pin}</td>
                                                        <td data-label="Status" className="col-status" style={{padding: '8px'}}>{p.status}</td>
                                                        <td data-label="Expires" className="col-expires hide-mobile" style={{padding: '8px'}}>{p.expiresAt ? new Date(p.expiresAt).toLocaleString() : ''}</td>
                                                        <td data-label="Used At" className="col-usedat hide-mobile" style={{padding: '8px'}}>{p.usedAt ? new Date(p.usedAt).toLocaleString() : ''}</td>
                                                    </tr>
                                                    <tr className="pin-details-row" id={`pin-details-${p.id}`} key={(p.id || idx) + '-details'} style={{display: expandedPinId === p.id ? 'table-row' : 'none'}}>
                                                        <td colSpan={8} style={{padding: 0}}>
                                                            <div style={{padding: '10px 12px', background: '#fafafa'}}>
                                                                <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: 8}}>
                                                                    <div><strong>User:</strong> <span className="truncate" title={p.userEmail || p.userId}>{p.userEmail || p.userId || '-'}</span></div>
                                                                    <div><strong>User Code:</strong> {p.userCode || '-'}</div>
                                                                    <div><strong>Key:</strong> <span className="truncate" title={p.key}>{p.key || '-'}</span></div>
                                                                    <div style={{display: 'flex', gap: 12}}><div><strong>PIN:</strong> {p.pin}</div><div><strong>Status:</strong> {p.status}</div></div>
                                                                    <div><strong>Expires:</strong> {p.expiresAt ? new Date(p.expiresAt).toLocaleString() : '-'}</div>
                                                                    <div><strong>Used At:</strong> {p.usedAt ? new Date(p.usedAt).toLocaleString() : '-'}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    </>
                                                ))}
                                            </tbody>
                                        </table>
                                    {/* Pagination footer */}
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12}}>
                                        <div style={{fontSize: 13, color: '#444'}}>Total: {pinTotal}</div>
                                        <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                                            <button onClick={prevPinPage} disabled={pinPage <= 1} style={{padding: '6px 10px'}}>Prev</button>
                                            <div style={{fontSize:13}}>Page {pinPage} / {Math.max(1, Math.ceil(pinTotal / pinLimit))}</div>
                                            <button onClick={nextPinPage} disabled={pinPage >= Math.ceil(pinTotal / pinLimit)} style={{padding: '6px 10px'}}>Next</button>
                                        </div>
                                    </div>
                                    </div>
                                ) : (
                                    <div style={{textAlign: 'center', padding: '40px'}}>No PIN codes found.</div>
                                )}
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}